import React, { useState, useEffect, useRef } from "react";
import { loadAll, saveKey, subscribeToChanges } from "./firebase";

// ── Default Data ────────────────────────────────────────────────────────────
const defaultInfo = { partner1: "Khalib", partner2: "Zoë", date: "", venue: "", theme: "" };

const defaultGuests = [];

const defaultTables = [];

const defaultVendors = [];

const defaultChecklist = [];

const defaultRegistry = [];

const defaultVenues = [];

const defaultBudget = {
  total: 0,
  categories: []
};

const defaultTimeline = [];

// ── Helpers ──────────────────────────────────────────────────────────────────
const MEAL_COLORS = { chicken: "var(--rose)", fish: "#6ca8c8", vegetarian: "#6cc87a", vegan: "#b6c86c", "": "#aaa" };
const RSVP_COLORS = { confirmed: "#6cc87a", pending: "var(--gold)", declined: "#8a4040", "": "#aaa" };
const PRIORITY_COLORS = { high: "#8a4040", medium: "var(--gold)", low: "#6cb8f0" };
const CAT_COLORS = { ceremony: "#d4a0d0", reception: "#a0c4d4", dining: "#d4c4a0", logistics: "#c4d4a0" };

const uid = () => Date.now() + Math.random();
const currency = n => { const num = Number(n); return isNaN(num) ? "—" : "$" + num.toLocaleString(); };

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / 86400000);
}


// ── Whimsical SVG Icons ───────────────────────────────────────────────────────
const Icon = ({ name, size = 18, color = "currentColor" }) => {
  const s = { width: size, height: size, display: "inline-block", flexShrink: 0 };
  const icons = {
    home:    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M3 12L12 3l9 9"/><path d="M5 10v9a1 1 0 001 1h4v-4h4v4h4a1 1 0 001-1v-9"/></svg>,
    guests:  <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><circle cx="9" cy="7" r="3"/><path d="M3 21v-2a5 5 0 0110 0v2"/><path d="M16 3.13a4 4 0 010 7.75"/><path d="M21 21v-2a4 4 0 00-3-3.85"/></svg>,
    seat:    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M5 3v9"/><path d="M19 3v9"/><path d="M3 12h18"/><path d="M5 21v-3a3 3 0 013-3h8a3 3 0 013 3v3"/></svg>,
    vendor:  <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>,
    check:   <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
    gift:    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>,
    venue:   <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-4a3 3 0 016 0v4"/><rect x="9" y="10" width="6" height="4"/></svg>,
    budget:  <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v2m0 8v2m-4-7h2a2 2 0 100-4H8m0 4h3a2 2 0 110 4H8"/></svg>,
    time:    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    sparkle: <svg style={s} viewBox="0 0 24 24" fill={color} stroke="none"><path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5z"/><path d="M19 14l.75 2.25L22 17l-2.25.75L19 20l-.75-2.25L16 17l2.25-.75z" opacity=".6"/><path d="M5 17l.5 1.5L7 19l-1.5.5L5 21l-.5-1.5L3 19l1.5-.5z" opacity=".4"/></svg>,
    moon:    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>,
    feather: <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M20.24 12.24a6 6 0 00-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/><line x1="17.5" y1="15" x2="9" y2="15"/></svg>,
    leaf:    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M11 20A7 7 0 0118 6H4a2 2 0 00-2 2v2a16 16 0 0016 16h2a2 2 0 002-2v-2.586a1 1 0 00-.293-.707l-1.914-1.914A7.003 7.003 0 0111 20z"/></svg>,
    star:    <svg style={s} viewBox="0 0 24 24" fill={color} stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    edit:    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    trash:   <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6m4-6v6"/><path d="M9 6V4h6v2"/></svg>,
    plus:    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
    close:   <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    wand:    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><line x1="3" y1="21" x2="21" y2="3"/><path d="M17 3l4 4-1 1-4-4zM3 17l4 4 1-1-4-4z"/><circle cx="6" cy="6" r="1" fill={color}/><circle cx="18" cy="18" r="1" fill={color}/><circle cx="6" cy="18" r="1" fill={color} opacity=".5"/><circle cx="18" cy="6" r="1" fill={color} opacity=".5"/></svg>,
    ring:    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><circle cx="12" cy="14" r="7"/><path d="M9 14a3 3 0 006 0"/><path d="M9 3l1 4h4l1-4"/><path d="M9 3h6"/></svg>,
    dove:    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M16 7c0-1.1-.9-2-2-2h-2L8 9H5a2 2 0 00-2 2v1l3 3h2l1 3 3-2 1-2h2a2 2 0 002-2V9a2 2 0 00-2-2z"/><line x1="12" y1="5" x2="12" y2="2"/><line x1="16" y1="3" x2="14" y2="5"/></svg>,
    flower:  <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><circle cx="12" cy="12" r="2"/><path d="M12 2a3 3 0 000 6 3 3 0 000-6z" opacity=".7"/><path d="M12 16a3 3 0 000 6 3 3 0 000-6z" opacity=".7"/><path d="M2 12a3 3 0 006 0 3 3 0 00-6 0z" opacity=".7"/><path d="M16 12a3 3 0 006 0 3 3 0 00-6 0z" opacity=".7"/><path d="M5 5a3 3 0 004 4 3 3 0 00-4-4z" opacity=".5"/><path d="M15 15a3 3 0 004 4 3 3 0 00-4-4z" opacity=".5"/><path d="M5 19a3 3 0 004-4 3 3 0 00-4 4z" opacity=".5"/><path d="M15 9a3 3 0 004-4 3 3 0 00-4 4z" opacity=".5"/></svg>,
    map:     <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    scroll:  <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/><line x1="9" y1="9" x2="11" y2="9"/></svg>,
  };
  return icons[name] || icons.sparkle;
};

// ── Fairy Dust particles ──────────────────────────────────────────────────────
const FairyDust = () => (
  <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none", overflow:"hidden" }} viewBox="0 0 800 120" preserveAspectRatio="xMidYMid slice">
    {[
      [60,20,2.5],[140,70,1.5],[250,30,3],[380,80,2],[490,15,1.8],[560,55,2.8],[670,35,1.5],[740,75,2.2],
      [100,50,1.2],[320,20,1.8],[430,60,1],[620,20,2],[200,85,1.4],[710,50,1.2],[30,65,1.8],[550,90,1.5]
    ].map(([cx,cy,r],i) => (
      <circle key={i} cx={cx} cy={cy} r={r} fill="rgba(210,185,145,0.55)">
        <animate attributeName="opacity" values="0.2;0.8;0.2" dur={`${2+i*0.3}s`} repeatCount="indefinite"/>
        <animate attributeName="cy" values={`${cy};${cy-4};${cy}`} dur={`${3+i*0.2}s`} repeatCount="indefinite"/>
      </circle>
    ))}
  </svg>
);

// ── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&family=Raleway:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --forest:      #131813;
    --forest-mid:  #1a221a;
    --forest-card: #1e281e;
    --forest-lift: #243024;
    --border:      rgba(175,155,105,0.18);
    --border-glow: rgba(175,155,105,0.42);
    --rose:        #c8927a;
    --rose-light:  #ddb09a;
    --rose-dim:    #8a5a48;
    --gold:        #c8a96e;
    --sage:        #7aaa80;
    --sage-dim:    #4a7050;
    --ink:         #e8ddc8;
    --ink-dim:     #9a9080;
    --ink-faint:   #585048;
    --shadow:      0 8px 40px rgba(0,0,0,0.55);
    --shadow-sm:   0 2px 16px rgba(0,0,0,0.38);
    --radius:      14px;
    --radius-sm:   8px;
  }

  body { font-family: 'Raleway', sans-serif; background: var(--forest); color: var(--ink); min-height: 100vh; }

  .app { min-height: 100vh; display: flex; flex-direction: column; }

  /* Header */
  .header {
    background: linear-gradient(160deg, #0d1210 0%, #131a13 40%, #192218 100%); border-bottom: 1px solid var(--border);
    padding: 0;
    position: relative;
    overflow: hidden;
  }
  .header::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at 15% 50%, rgba(175,155,105,0.06) 0%, transparent 55%), radial-gradient(ellipse at 85% 30%, rgba(100,140,100,0.05) 0%, transparent 50%);
  }
  .header-inner {
    position: relative;
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px 32px;
    flex-wrap: wrap; gap: 16px;
  }
  .header-title { display: flex; align-items: center; gap: 16px; }
  .header-crest {
    width: 54px; height: 54px; border-radius: 50%;
    background: rgba(200,169,110,0.07); border: 1px solid rgba(200,169,110,0.3);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Cormorant Garamond', serif;
    font-size: 22px; color: #fff; letter-spacing: 2px;
  }
  .header-names {
    font-family: 'Cormorant Garamond', serif;
    font-size: 28px; font-weight: 400; color: var(--rose-light); letter-spacing: 1px;
    line-height: 1.1;
  }
  .header-names span { font-size: 14px; font-family: 'Raleway', sans-serif; font-weight: 300;
    color: var(--ink-faint); letter-spacing: 3px; display: block; margin-top: 5px; }
  .header-date {
    display: flex; align-items: center; gap: 24px;
  }
  .countdown-box {
    text-align: center;
    background: rgba(200,169,110,0.07); border: 1px solid rgba(200,169,110,0.22);
    border-radius: 12px; padding: 10px 20px;
  }
  .countdown-num { font-family: 'Cormorant Garamond', serif; font-size: 38px; color: var(--gold); line-height: 1; }
  .countdown-label { font-size: 9px; color: var(--ink-faint); letter-spacing: 3px; text-transform: uppercase; margin-top: 3px; }
  .header-edit-btn {
    display: flex; align-items: center; gap: 7px;
    background: rgba(200,169,110,0.07); border: 1px solid rgba(200,169,110,0.22);
    color: var(--gold); padding: 9px 16px; border-radius: 8px;
    cursor: pointer; font-size: 11px; font-family: 'Raleway', sans-serif; letter-spacing: 1.5px; text-transform: uppercase;
    transition: all 0.2s;
  }
  .header-edit-btn:hover { background: rgba(200,169,110,0.14); border-color: var(--border-glow); }

  /* ── Top group nav — horizontal row, gold glow on active ── */
  .nav {
    background: rgba(19,24,19,0.97); backdrop-filter: blur(10px);
    border-bottom: 1px solid var(--border);
    display: flex; flex-direction: row; flex-wrap: nowrap;
    overflow-x: auto; padding: 0 20px;
    position: sticky; top: 0; z-index: 101;
  }
  .nav::-webkit-scrollbar { display: none; }
  .nav-item {
    display: flex; align-items: center; gap: 8px;
    padding: 15px 16px; cursor: pointer; white-space: nowrap;
    border-bottom: 2px solid transparent;
    font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--ink-faint); transition: all 0.2s; flex-shrink: 0;
  }
  .nav-item:hover { color: var(--rose-light); }
  .nav-item.active {
    color: var(--gold);
    border-bottom-color: var(--gold);
    text-shadow: 0 0 12px rgba(200,169,110,0.5);
  }
  .nav-item.bridal-item { margin-left: auto; text-decoration: none; }
  .nav-item.bridal-item.active { color: var(--gold); border-bottom-color: var(--gold); }
  .nav-icon { font-size: 16px; }

  /* ── Body layout: sidebar (when group has multiple tabs) + content ── */
  .dash-body { display: flex; flex-direction: row; flex: 1; align-items: flex-start; min-height: 0; }
  .dash-sidebar {
    width: 220px; flex: 0 0 220px;
    background: rgba(15,19,15,0.6);
    border-right: 1px solid var(--border);
    padding: 20px 0;
    align-self: stretch;
  }
  .sidebar-item {
    display: flex; align-items: center; gap: 10px;
    padding: 11px 22px; cursor: pointer;
    font-size: 12px; font-weight: 600; letter-spacing: 0.5px;
    color: var(--ink-dim); transition: all 0.15s;
    border-left: 2px solid transparent;
  }
  .sidebar-item:hover { color: var(--rose-light); background: rgba(255,255,255,0.02); }
  .sidebar-item.active {
    color: var(--rose);
    background: rgba(200,145,122,0.08);
    border-left-color: var(--rose);
  }

  /* Main */
  .main { flex: 1; min-width: 0; padding: 28px 24px; max-width: 1160px; margin: 0 auto; width: 100%; padding-top: 24px; }

  /* Cards */
  .card {
    background: var(--forest-card); border-radius: var(--radius);
    border: 1px solid var(--border); box-shadow: var(--shadow-sm);
    padding: 22px;
  }
  .card-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 20px; font-weight: 500; color: var(--rose-light);
    margin-bottom: 18px; display: flex; align-items: center; gap: 10px;
  }
  .card-title .icon { font-size: 18px; }

  /* Stat cards */
  .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 16px; margin-bottom: 28px; }
  .stat-card {
    background: var(--forest-card); border-radius: var(--radius);
    border: 1px solid var(--border); padding: 20px; text-align: center;
    box-shadow: var(--shadow-sm);
  }
  .stat-num { font-family: 'Cormorant Garamond', serif; font-size: 38px; color: var(--gold); line-height: 1; }
  .stat-label { font-size: 10px; color: var(--ink-dim); letter-spacing: 2.5px; text-transform: uppercase; margin-top: 5px; }
  .stat-sub { font-size: 12px; color: var(--sage); margin-top: 4px; }

  /* Buttons */
  .btn {
    padding: 9px 18px; border-radius: var(--radius-sm); cursor: pointer;
    font-family: 'Raleway', sans-serif; font-size: 13px; font-weight: 500;
    letter-spacing: 0.5px; transition: all 0.2s; border: none;
  }
  .btn-primary { background: linear-gradient(135deg, #7a4535, #5a3025); color: var(--ink); }
  .btn-primary:hover { background: linear-gradient(135deg, var(--rose), var(--rose-dim)); }
  .btn-secondary { background: transparent; border: 1px solid var(--border); color: var(--ink-dim); }
  .btn-secondary:hover { border-color: var(--border-glow); color: var(--rose); }
  .btn-danger { background: #8a3030; color: var(--ink); }
  .btn-danger:hover { background: #a04040; }
  .btn-sm { padding: 5px 12px; font-size: 12px; }
  .btn-icon { padding: 7px; background: transparent; border: 1px solid var(--border); color: var(--ink-faint); border-radius: 6px; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; }
  .btn-icon:hover { border-color: var(--border-glow); color: var(--rose); }

  /* Form elements */
  .form-row { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
  .form-group { display: flex; flex-direction: column; gap: 5px; flex: 1; min-width: 140px; }
  .form-group label { font-size: 10px; color: var(--ink-dim); letter-spacing: 1.5px; text-transform: uppercase; }
  .form-group input, .form-group select, .form-group textarea {
    padding: 9px 12px; border-radius: var(--radius-sm);
    border: 1px solid var(--border); background: var(--forest-lift);
    font-family: 'Raleway', sans-serif; font-size: 13px; color: var(--ink);
    transition: border 0.2s; outline: none;
  }
  .form-group input:focus, .form-group select:focus, .form-group textarea:focus { border-color: var(--border-glow); }
  .form-group textarea { resize: vertical; min-height: 72px; }

  /* Badge */
  .badge {
    display: inline-block; padding: 3px 10px; border-radius: 20px;
    font-size: 11px; font-weight: 600; letter-spacing: 0.5px;
  }

  /* Table */
  .table-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th {
    text-align: left; padding: 10px 14px; border-bottom: 2px solid var(--border);
    font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--ink-dim);
    font-weight: 600; white-space: nowrap;
  }
  td { padding: 11px 14px; border-bottom: 1px solid rgba(175,155,105,0.07); vertical-align: middle; }
  tr:hover td { background: rgba(245,237,224,0.5); }
  tr:last-child td { border-bottom: none; }

  /* Modal */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.72);
    backdrop-filter: blur(4px); z-index: 1000;
    display: flex; align-items: center; justify-content: center;
    padding: 24px; animation: fadeIn 0.15s ease;
  }
  .modal {
    background: var(--forest-mid); border-radius: var(--radius);
    border: 1px solid var(--border); box-shadow: var(--shadow);
    padding: 28px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto;
    animation: slideUp 0.2s ease;
  }
  .modal-title { font-family: 'Cormorant Garamond', serif; font-size: 22px; color: var(--rose-light); margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }
  .modal-footer { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
  @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
  @keyframes slideUp { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }

  /* Seating chart */
  .seating-container { display: flex; gap: 20px; flex-wrap: wrap; }
  .seating-canvas-wrap {
    flex: 1; min-width: 400px; background: var(--forest-card);
    border-radius: var(--radius); border: 1px dashed rgba(175,155,105,0.2);
    position: relative; min-height: 520px; overflow: hidden;
  }
  .seating-stage {
    position: absolute; top: 20px; left: 50%; transform: translateX(-50%);
    background: linear-gradient(135deg, #22180a, #1a130a);
    border: 1px solid rgba(200,169,110,0.3);
    color: var(--gold); padding: 7px 28px; border-radius: 30px;
    font-size: 10px; letter-spacing: 3px; font-weight: 600;
  }
  .seat-table {
    position: absolute; cursor: move; user-select: none;
    display: flex; flex-direction: column; align-items: center; gap: 4px;
    transition: filter 0.15s;
  }
  .seat-table:hover { filter: brightness(1.05); }
  .seat-table-circle {
    border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
    background: var(--forest-lift);
    box-shadow: 0 0 20px rgba(0,0,0,0.4);
    font-size: 10px; font-weight: 600; color: var(--gold);
  }
  .seat-table-label { font-size: 10px; font-weight: 600; color: var(--rose-light); letter-spacing: 0.5px; text-align: center; white-space: nowrap; max-width: 80px; }
  .seating-sidebar {
    width: 260px; display: flex; flex-direction: column; gap: 12px;
  }
  .seating-unassigned {
    background: var(--forest-card); border-radius: var(--radius);
    border: 1px solid var(--border); padding: 14px; max-height: 300px; overflow-y: auto;
  }
  .unassigned-guest {
    padding: 7px 10px; background: var(--forest-lift);
    border-radius: 6px; margin-bottom: 6px; font-size: 12px;
    border: 1px solid var(--border); cursor: pointer; transition: all 0.15s;
    display: flex; align-items: center; justify-content: space-between; color: var(--ink);
  }
  .unassigned-guest:hover { border-color: var(--border-glow); color: var(--rose); }

  /* Budget bar */
  .budget-bar-wrap { margin-bottom: 12px; }
  .budget-bar-header { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 13px; }
  .budget-bar-track { height: 8px; background: rgba(255,255,255,0.05); border-radius: 4px; overflow: hidden; }
  .budget-bar-fill {
    height: 100%; border-radius: 4px;
    transition: width 0.4s ease;
  }

  /* Timeline */
  .timeline-item {
    display: flex; gap: 16px; padding: 14px 0;
    border-bottom: 1px solid rgba(175,155,105,0.07);
    align-items: flex-start;
  }
  .timeline-time { font-family: 'Cormorant Garamond', serif; font-size: 18px; color: var(--gold); min-width: 72px; padding-top: 2px; font-weight: 500; }
  .timeline-dot {
    width: 12px; height: 12px; border-radius: 50%; margin-top: 6px; flex-shrink: 0;
    border: 2px solid var(--rose);
  }
  .timeline-content { flex: 1; }
  .timeline-event { font-weight: 600; font-size: 14px; color: var(--ink); }
  .timeline-note { font-size: 12px; color: var(--ink-dim); margin-top: 3px; }

  /* Registry */
  .registry-item {
    display: flex; align-items: center; gap: 14px;
    padding: 13px; background: var(--forest-lift);
    border-radius: var(--radius-sm); border: 1px solid var(--border);
    margin-bottom: 10px; transition: all 0.15s;
  }
  .registry-item.purchased { opacity: 0.6; }
  .registry-item:hover { border-color: var(--border-glow); }
  .registry-check { width: 22px; height: 22px; border-radius: 50%; border: 1.5px solid var(--border); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; flex-shrink: 0; font-size: 12px; }
  .registry-check.checked { background: var(--sage-dim); border-color: var(--sage); color: #fff; }

  /* Venue */
  .venue-card {
    background: var(--forest-card); border-radius: var(--radius);
    border: 1px solid var(--border); padding: 20px; margin-bottom: 16px;
    transition: all 0.2s;
  }
  .venue-card:hover { border-color: var(--border-glow); box-shadow: 0 0 20px rgba(175,155,105,0.08); }
  .venue-status {
    padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; letter-spacing: 1px;
  }
  .stars { color: var(--gold); font-size: 13px; letter-spacing: 1px; }

  /* Section header */
  .section-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 20px; flex-wrap: wrap; gap: 12px;
  }

  /* Filter bar */
  .filter-bar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
  .filter-chip {
    padding: 5px 14px; border-radius: 20px; font-size: 12px; cursor: pointer;
    border: 1.5px solid var(--border); color: var(--ink-dim); background: transparent;
    transition: all 0.15s; font-family: 'Raleway', sans-serif;
  }
  .filter-chip.active { border-color: var(--border-glow); color: var(--rose); background: rgba(200,145,122,0.08); }

  /* Progress ring */
  .progress-section { margin-bottom: 20px; }
  .progress-bar-track { height: 7px; background: rgba(255,255,255,0.05); border-radius: 3px; overflow: hidden; margin-top: 8px; }
  .progress-bar-fill { height: 100%; border-radius: 3px; background: linear-gradient(90deg, var(--rose), var(--rose-dim)); transition: width 0.4s; }

  /* Checklist */
  .checklist-item { display: flex; align-items: flex-start; gap: 12px; padding: 11px; border-radius: var(--radius-sm); border: 1px solid var(--border); margin-bottom: 7px; background: var(--forest-lift); transition: all 0.15s; }
  .checklist-item.done { opacity: 0.55; }
  .check-box {
    width: 20px; height: 20px; border-radius: 4px; border: 2px solid var(--border);
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    flex-shrink: 0; transition: all 0.2s; font-size: 11px; margin-top: 1px;
  }
  .check-box.checked { background: var(--sage-dim); border-color: var(--sage); color: var(--forest); }

  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  @media (max-width: 768px) { .grid-2 { grid-template-columns: 1fr; } }
  .grid-3 { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px; }

  .tag { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; }

  .empty-state { text-align: center; padding: 40px; color: var(--ink-dim); }
  .empty-state .e-icon { font-size: 40px; margin-bottom: 10px; }
  .empty-state p { font-size: 14px; }

  .info-toast { background: rgba(122,170,128,0.1); border: 1px solid rgba(122,170,128,0.22); color: var(--sage); padding: 10px 16px; border-radius: 8px; font-size: 12px; margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }

  input[type="checkbox"] { accent-color: var(--rose); width: 16px; height: 16px; cursor: pointer; }
  ::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-track { background: var(--forest); } ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }

  .vendor-category-badge { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 10px; font-weight: 600; background: rgba(200,145,122,0.12); color: var(--rose); }
`;

// ── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ guests, vendors, checklist, budget, weddingInfo }) {
  const confirmed = guests.filter(g => g.rsvp === "confirmed").length;
  const pending = guests.filter(g => g.rsvp === "pending").length;
  const doneItems = checklist.filter(c => c.done).length;
  const progress = checklist.length ? Math.round((doneItems / checklist.length) * 100) : 0;
  const totalSpent = budget.categories.reduce((s, c) => s + c.spent, 0);
  const contracted = vendors.filter(v => v.contracted).length;
  const days = weddingInfo.date ? daysUntil(weddingInfo.date) : null;

  return (
    <div>
      <div className="info-toast">
        ✦ Welcome to your wedding dashboard! All data is synced — both partners can edit from any device.
      </div>
      <div className="stats-grid">
        {days !== null && (
          <div className="stat-card" style={{ gridColumn: "span 1" }}>
            <div className="stat-num">{days > 0 ? days : 0}</div>
            <div className="stat-label">Days to Go</div>
            <div className="stat-sub">{weddingInfo.date || "Set your date"}</div>
          </div>
        )}
        <div className="stat-card">
          <div className="stat-num">{guests.length}</div>
          <div className="stat-label">Total Guests</div>
          <div className="stat-sub" style={{ color: "#6cc87a" }}>{confirmed} confirmed</div>
        </div>
        <div className="stat-card">
          <div className="stat-num">{pending}</div>
          <div className="stat-label">RSVP Pending</div>
          <div className="stat-sub">{guests.filter(g => g.rsvp === "declined").length} declined</div>
        </div>
        <div className="stat-card">
          <div className="stat-num">{vendors.length}</div>
          <div className="stat-label">Vendors</div>
          <div className="stat-sub">{contracted} contracted</div>
        </div>
        <div className="stat-card">
          <div className="stat-num">{progress}%</div>
          <div className="stat-label">Planning Done</div>
          <div className="stat-sub">{doneItems} of {checklist.length} tasks</div>
        </div>
        <div className="stat-card">
          <div className="stat-num">{currency(totalSpent)}</div>
          <div className="stat-label">Total Spent</div>
          <div className="stat-sub">of {currency(budget.total)} budget</div>
        </div>
      </div>

      <div className="grid-2">
        {/* Budget overview */}
        <div className="card">
          <div className="card-title"><Icon name="budget" size={16} color="var(--gold)"/>Budget Snapshot</div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
              <span>Overall</span>
              <span style={{ fontWeight: 600 }}>{currency(totalSpent)} / {currency(budget.total)}</span>
            </div>
            <div className="progress-bar-track">
              <div className="progress-bar-fill" style={{ width: `${Math.min(100, (totalSpent/budget.total)*100)}%` }} />
            </div>
          </div>
          {budget.categories.slice(0, 5).map(cat => (
            <div key={cat.id} className="budget-bar-wrap">
              <div className="budget-bar-header">
                <span style={{ fontSize: 12 }}>{cat.name}</span>
                <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>{currency(cat.spent)} / {currency(cat.budgeted)}</span>
              </div>
              <div className="budget-bar-track">
                <div className="budget-bar-fill" style={{
                  width: `${Math.min(100, cat.budgeted ? (cat.spent/cat.budgeted)*100 : 0)}%`,
                  background: cat.spent > cat.budgeted ? "#8a4040" : "linear-gradient(90deg, var(--sage), var(--sage-dim))"
                }} />
              </div>
            </div>
          ))}
        </div>

        {/* Upcoming tasks */}
        <div className="card">
          <div className="card-title"><Icon name="check" size={16} color="var(--gold)"/>Upcoming Tasks</div>
          {checklist.filter(t => !t.done).sort((a, b) => {
            const pa = { high: 0, medium: 1, low: 2 };
            return pa[a.priority] - pa[b.priority];
          }).slice(0, 6).map(task => (
            <div key={task.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid rgba(175,155,105,0.07)" }}>
              <span style={{ fontSize: 8, width: 8, height: 8, borderRadius: "50%", background: PRIORITY_COLORS[task.priority], flexShrink: 0, display: "inline-block" }} />
              <span style={{ flex: 1, fontSize: 13 }}>{task.task}</span>
              <span className="badge" style={{ background: "rgba(175,155,105,0.12)", color: "var(--ink-dim)", fontSize: 10 }}>{task.category}</span>
            </div>
          ))}
        </div>

        {/* Vendors overview */}
        <div className="card">
          <div className="card-title"><span className="icon">🤝</span>Vendor Status</div>
          {vendors.slice(0, 5).map(v => (
            <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: "1px solid rgba(175,155,105,0.07)" }}>
              <span className="vendor-category-badge">{v.category}</span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{v.name}</span>
              <span className="badge" style={{ background: v.contracted ? "rgba(108,200,122,0.15)" : "rgba(240,192,96,0.15)", color: v.contracted ? "#3a7a3a" : "#8a6a10", fontSize: 10 }}>
                {v.contracted ? "✓ Signed" : "Pending"}
              </span>
            </div>
          ))}
        </div>

        {/* RSVP Breakdown */}
        <div className="card">
          <div className="card-title"><span className="icon">📨</span>RSVP Breakdown</div>
          {[
            { label: "Confirmed", count: confirmed, color: "#6cc87a" },
            { label: "Pending", count: pending, color: "var(--gold)" },
            { label: "Declined", count: guests.filter(g => g.rsvp === "declined").length, color: "#8a4040" },
          ].map(r => (
            <div key={r.label} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 13 }}>
                <span>{r.label}</span>
                <strong>{r.count} guests</strong>
              </div>
              <div className="budget-bar-track">
                <div className="budget-bar-fill" style={{ width: guests.length ? `${(r.count/guests.length)*100}%` : "0%", background: r.color }} />
              </div>
            </div>
          ))}
          <div style={{ margin: "16px 0 10px", borderTop: "1px solid var(--border)", paddingTop: 14 }}>
            <div style={{ fontSize: 12, color: "var(--ink-dim)", marginBottom: 8 }}>Meal Preferences (confirmed)</div>
            {["chicken", "fish", "vegetarian", "vegan"].map(m => {
              const cnt = guests.filter(g => g.rsvp === "confirmed" && g.meal === m).length;
              if (!cnt) return null;
              return (
                <div key={m} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: MEAL_COLORS[m], display: "inline-block" }} />
                  <span style={{ fontSize: 12, textTransform: "capitalize", flex: 1 }}>{m}</span>
                  <strong style={{ fontSize: 12 }}>{cnt}</strong>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Guest List Tab ────────────────────────────────────────────────────────────
function GuestTab({ guests, setGuests, tables }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const blank = { name: "", email: "", phone: "", side: "bride", rsvp: "pending", meal: "", tableId: "", plusOne: false, notes: "" };
  const [form, setForm] = useState(blank);

  const filtered = guests.filter(g => {
    if (filter !== "all" && g.rsvp !== filter) return false;
    if (search && !g.name.toLowerCase().includes(search.toLowerCase()) && !g.email.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  function openAdd() { setForm(blank); setEditing(null); setShowModal(true); }
  function openEdit(g) { setForm({ ...g }); setEditing(g.id); setShowModal(true); }
  function save() {
    if (!form.name) return;
    if (editing) setGuests(prev => prev.map(g => g.id === editing ? { ...form, id: editing } : g));
    else setGuests(prev => [...prev, { ...form, id: uid() }]);
    setShowModal(false);
  }
  function remove(id) { setGuests(prev => prev.filter(g => g.id !== id)); }

  return (
    <div>
      <div className="section-header">
        <div className="card-title" style={{ margin: 0 }}><Icon name="guests" size={16} color="var(--gold)"/>Guest List ({guests.length})</div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Guest</button>
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <input className="form-group input" style={{ padding: "8px 14px", borderRadius: 8, border: "1.5px solid var(--border)", fontFamily: "Raleway", flex: 1, minWidth: 200 }}
          placeholder="🔍  Search guests…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div className="filter-bar">
        {["all", "confirmed", "pending", "declined"].map(f => (
          <button key={f} className={`filter-chip ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
            <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600 }}>
              ({f === "all" ? guests.length : guests.filter(g => g.rsvp === f).length})
            </span>
          </button>
        ))}
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Side</th><th>RSVP</th><th>Meal</th><th>Table</th><th>Contact</th><th>+1</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(g => {
                const tbl = tables.find(t => t.id === g.tableId);
                return (
                  <tr key={g.id}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{g.name}</div>
                      {g.notes && <div style={{ fontSize: 11, color: "var(--ink-dim)" }}>{g.notes}</div>}
                    </td>
                    <td><span className="badge" style={{ background: g.side === "bride" ? "rgba(232,196,176,0.3)" : "rgba(122,155,122,0.2)", color: g.side === "bride" ? "var(--rose-dim)" : "var(--sage-dim)" }}>{g.side}</span></td>
                    <td><span className="badge" style={{ background: `${RSVP_COLORS[g.rsvp]}22`, color: RSVP_COLORS[g.rsvp] }}>{g.rsvp || "—"}</span></td>
                    <td><span style={{ textTransform: "capitalize", fontSize: 12 }}>{g.meal || "—"}</span></td>
                    <td style={{ fontSize: 12, color: "var(--ink-dim)" }}>{tbl ? tbl.name : "Unassigned"}</td>
                    <td style={{ fontSize: 12, color: "var(--ink-dim)" }}>{g.email || g.phone || "—"}</td>
                    <td>{g.plusOne ? "✓" : ""}</td>
                    <td>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn-icon" onClick={() => openEdit(g)}>✏️</button>
                        <button className="btn-icon" onClick={() => remove(g.id)}><Icon name="trash" size={13} color="currentColor"/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="empty-state"><Icon name="guests" size={36} color="var(--ink-faint)"/><p>No guests found</p></div>}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">{editing ? "Edit Guest" : "Add Guest"}</div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>Full Name *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Jane Smith" />
              </div>
              <div className="form-group">
                <label>Side</label>
                <select value={form.side} onChange={e => setForm(p => ({ ...p, side: e.target.value }))}>
                  <option value="bride">Bride's Side</option>
                  <option value="groom">Groom's Side</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@email.com" />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="555-0100" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>RSVP Status</label>
                <select value={form.rsvp} onChange={e => setForm(p => ({ ...p, rsvp: e.target.value }))}>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
              <div className="form-group">
                <label>Meal Choice</label>
                <select value={form.meal} onChange={e => setForm(p => ({ ...p, meal: e.target.value }))}>
                  <option value="">— Select —</option>
                  <option value="chicken">Chicken</option>
                  <option value="fish">Fish</option>
                  <option value="vegetarian">Vegetarian</option>
                  <option value="vegan">Vegan</option>
                </select>
              </div>
              <div className="form-group">
                <label>Table</label>
                <select value={form.tableId} onChange={e => setForm(p => ({ ...p, tableId: Number(e.target.value) }))}>
                  <option value="">Unassigned</option>
                  {tables.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row" style={{ alignItems: "center" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={form.plusOne} onChange={e => setForm(p => ({ ...p, plusOne: e.target.checked }))} />
                Bringing a +1
              </label>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Notes / Dietary Restrictions</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Allergies, special needs…" />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editing ? "Save Changes" : "Add Guest"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Seating Chart ─────────────────────────────────────────────────────────────
function SeatingTab({ guests, setGuests, tables, setTables }) {
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [selectedTable, setSelectedTable] = useState(null);
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableForm, setTableForm] = useState({ name: "", capacity: 8 });
  const [editingTable, setEditingTable] = useState(null);

  const unassigned = guests.filter(g => !g.tableId && g.rsvp !== "declined");

  function onMouseDown(e, tableId) {
    const rect = e.currentTarget.getBoundingClientRect();
    setDragging(tableId);
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    e.preventDefault();
  }

  function onMouseMove(e) {
    if (!dragging) return;
    const wrap = document.getElementById("seating-canvas");
    if (!wrap) return;
    const wRect = wrap.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - wRect.left - dragOffset.x, wRect.width - 90));
    const y = Math.max(60, Math.min(e.clientY - wRect.top - dragOffset.y, wRect.height - 90));
    setTables(prev => prev.map(t => t.id === dragging ? { ...t, x, y } : t));
  }

  function onMouseUp() { setDragging(null); }

  function assignGuest(guestId, tableId) {
    setGuests(prev => prev.map(g => g.id === guestId ? { ...g, tableId } : g));
  }
  function unassignGuest(guestId) {
    setGuests(prev => prev.map(g => g.id === guestId ? { ...g, tableId: null } : g));
  }

  function addTable() {
    if (!tableForm.name) return;
    if (editingTable) {
      setTables(prev => prev.map(t => t.id === editingTable ? { ...t, ...tableForm, capacity: Number(tableForm.capacity) } : t));
    } else {
      setTables(prev => [...prev, { ...tableForm, id: uid(), capacity: Number(tableForm.capacity), x: 200, y: 200 }]);
    }
    setShowTableModal(false);
    setTableForm({ name: "", capacity: 8 });
    setEditingTable(null);
  }
  function removeTable(id) {
    
    setTables(prev => prev.filter(t => t.id !== id));
    setGuests(prev => prev.map(g => g.tableId === id ? { ...g, tableId: null } : g));
    setSelectedTable(null);
  }

  const selTableData = tables.find(t => t.id === selectedTable);
  const selTableGuests = guests.filter(g => g.tableId === selectedTable);

  return (
    <div>
      <div className="section-header">
        <div className="card-title" style={{ margin: 0 }}><Icon name="seat" size={16} color="var(--gold)"/>Seating Chart</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => { setTableForm({ name: "", capacity: 8 }); setEditingTable(null); setShowTableModal(true); }}>+ Add Table</button>
        </div>
      </div>
      <div className="seating-container">
        <div
          id="seating-canvas"
          className="seating-canvas-wrap"
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          <div className="seating-stage">✦ ALTAR / STAGE ✦</div>
          {tables.map(table => {
            const tGuests = guests.filter(g => g.tableId === table.id);
            const pct = tGuests.length / table.capacity;
            const size = Math.max(70, Math.min(100, 60 + table.capacity * 4));
            return (
              <div
                key={table.id}
                className="seat-table"
                style={{ left: table.x, top: table.y }}
                onMouseDown={e => onMouseDown(e, table.id)}
                onClick={() => setSelectedTable(selectedTable === table.id ? null : table.id)}
              >
                <div
                  className="seat-table-circle"
                  style={{
                    width: size, height: size,
                    border: selectedTable === table.id ? "2.5px solid var(--rose-dim)" : `2.5px solid ${pct >= 1 ? "#8a4040" : "var(--rose)"}`,
                    background: selectedTable === table.id ? "linear-gradient(135deg, #f0c8b0, var(--rose-light))" : undefined,
                    flexDirection: "column", gap: 2
                  }}
                >
                  <div style={{ fontSize: 16 }}>🌸</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--rose-dim)" }}>{tGuests.length}/{table.capacity}</div>
                </div>
                <div className="seat-table-label">{table.name}</div>
              </div>
            );
          })}
          {tables.length === 0 && (
            <div className="empty-state" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)" }}>
              <Icon name="seat" size={36} color="var(--ink-faint)"/><p>Add tables to get started</p>
            </div>
          )}
        </div>

        <div className="seating-sidebar">
          {/* Table detail */}
          {selTableData && (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{selTableData.name}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-dim)" }}>{selTableGuests.length}/{selTableData.capacity} seated</div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button className="btn-icon" style={{ fontSize: 11 }} onClick={() => {
                    setTableForm({ name: selTableData.name, capacity: selTableData.capacity });
                    setEditingTable(selTableData.id);
                    setShowTableModal(true);
                  }}>✏️</button>
                  <button className="btn-icon" onClick={() => removeTable(selTableData.id)}><Icon name="trash" size={13} color="currentColor"/></button>
                </div>
              </div>
              {selTableGuests.map(g => (
                <div key={g.id} className="unassigned-guest">
                  <span>{g.name}</span>
                  <button style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-dim)", fontSize: 14 }} onClick={() => unassignGuest(g.id)} title="Unassign">×</button>
                </div>
              ))}
              {selTableGuests.length === 0 && <div style={{ fontSize: 12, color: "var(--ink-dim)", textAlign: "center", padding: "8px 0" }}>Empty table</div>}
            </div>
          )}

          {/* Unassigned */}
          <div className="seating-unassigned">
            <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "var(--ink-dim)", marginBottom: 10, fontWeight: 600 }}>
              Unassigned ({unassigned.length})
            </div>
            {unassigned.map(g => (
              <div key={g.id} className="unassigned-guest">
                <span>{g.name}</span>
                {selectedTable && selTableGuests.length < selTableData?.capacity && (
                  <button style={{ background: "var(--rose)", border: "none", cursor: "pointer", color: "#fff", fontSize: 11, padding: "2px 8px", borderRadius: 4 }}
                    onClick={() => assignGuest(g.id, selectedTable)}>
                    Seat
                  </button>
                )}
              </div>
            ))}
            {unassigned.length === 0 && <div style={{ fontSize: 12, color: "var(--sage)", textAlign: "center" }}>All guests seated! 🎉</div>}
          </div>

          <div style={{ fontSize: 12, color: "var(--ink-dim)", textAlign: "center", lineHeight: 1.5 }}>
            Click a table to select it, then use "Seat" to assign guests. Drag tables to rearrange.
          </div>
        </div>
      </div>

      {showTableModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowTableModal(false)}>
          <div className="modal" style={{ maxWidth: 360 }}>
            <div className="modal-title">{editingTable ? "Edit Table" : "Add Table"}</div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Table Name</label>
              <input value={tableForm.name} onChange={e => setTableForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Rose Table" />
            </div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Capacity</label>
              <input type="number" min={2} max={20} value={tableForm.capacity} onChange={e => setTableForm(p => ({ ...p, capacity: e.target.value }))} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowTableModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addTable}>{editingTable ? "Save" : "Add Table"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Vendors Tab ────────────────────────────────────────────────────────────────
function VendorTab({ vendors, setVendors }) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("All");
  const blank = { category: "Photography", name: "", contact: "", email: "", phone: "", price: "", deposit: "", depositPaid: false, contracted: false, notes: "", rating: 5 };
  const [form, setForm] = useState(blank);

  const categories = ["All", ...new Set(vendors.map(v => v.category))];
  const filtered = filter === "All" ? vendors : vendors.filter(v => v.category === filter);

  const CATS = ["Photography", "Catering", "Florist", "Music/DJ", "Venue", "Officiant", "Hair & Makeup", "Transport", "Cake", "Videography", "Lighting", "Stationery", "Rentals", "Honeymoon", "Other"];

  function openAdd() { setForm(blank); setEditing(null); setShowModal(true); }
  function openEdit(v) { setForm({ ...v }); setEditing(v.id); setShowModal(true); }
  function save() {
    if (!form.name) return;
    const v = { ...form, price: Number(form.price), deposit: Number(form.deposit), id: editing || uid() };
    if (editing) setVendors(prev => prev.map(x => x.id === editing ? v : x));
    else setVendors(prev => [...prev, v]);
    setShowModal(false);
  }
  function remove(id) { setVendors(prev => prev.filter(v => v.id !== id)); }

  const totalContracted = vendors.filter(v => v.contracted).reduce((s, v) => s + v.price, 0);
  const totalDeposits = vendors.filter(v => v.depositPaid).reduce((s, v) => s + v.deposit, 0);

  return (
    <div>
      <div className="section-header">
        <div className="card-title" style={{ margin: 0 }}><span className="icon">🤝</span>Vendors ({vendors.length})</div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Vendor</button>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-num" style={{ fontSize: 28 }}>{vendors.filter(v => v.contracted).length}</div>
          <div className="stat-label">Contracted</div>
        </div>
        <div className="stat-card">
          <div className="stat-num" style={{ fontSize: 28 }}>{currency(totalContracted)}</div>
          <div className="stat-label">Total Committed</div>
        </div>
        <div className="stat-card">
          <div className="stat-num" style={{ fontSize: 28 }}>{currency(totalDeposits)}</div>
          <div className="stat-label">Deposits Paid</div>
        </div>
      </div>

      <div className="filter-bar">
        {categories.map(c => (
          <button key={c} className={`filter-chip ${filter === c ? "active" : ""}`} onClick={() => setFilter(c)}>{c}</button>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {filtered.map(v => (
          <div key={v.id} className="card" style={{ padding: 18 }}>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span className="vendor-category-badge">{v.category}</span>
                  <span style={{ color: "var(--gold)", fontSize: 13 }}>{"★".repeat(v.rating)}{"☆".repeat(5 - v.rating)}</span>
                </div>
                <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 3 }}>{v.name}</div>
                <div style={{ fontSize: 12, color: "var(--ink-dim)" }}>{v.contact} {v.phone && `· ${v.phone}`}</div>
                {v.email && <div style={{ fontSize: 12, color: "var(--ink-dim)" }}>{v.email}</div>}
                {v.notes && <div style={{ fontSize: 12, color: "var(--sage)", marginTop: 6, fontStyle: "italic" }}>{v.notes}</div>}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 180 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "var(--ink-dim)" }}>Total Cost</span>
                  <strong>{currency(v.price)}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "var(--ink-dim)" }}>Deposit</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {currency(v.deposit)}
                    <span className="badge" style={{ fontSize: 10, background: v.depositPaid ? "rgba(108,200,122,0.15)" : "rgba(240,192,96,0.15)", color: v.depositPaid ? "#3a7a3a" : "#8a6a10" }}>
                      {v.depositPaid ? "Paid" : "Due"}
                    </span>
                  </span>
                </div>
                <span className="badge" style={{ alignSelf: "flex-start", background: v.contracted ? "rgba(108,200,122,0.15)" : "rgba(240,192,96,0.15)", color: v.contracted ? "#3a7a3a" : "#8a6a10" }}>
                  {v.contracted ? "✓ Contract Signed" : "⏳ Contract Pending"}
                </span>
                <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(v)}>Edit</button>
                  <button className="btn btn-secondary btn-sm" style={{ color: "#8a4040" }} onClick={() => remove(v.id)}>Remove</button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="empty-state card"><div className="e-icon">🤝</div><p>No vendors in this category</p></div>}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">{editing ? "Edit Vendor" : "Add Vendor"}</div>
            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  {CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 2 }}>
                <label>Business Name *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Vendor name" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Contact Person</label>
                <input value={form.contact} onChange={e => setForm(p => ({ ...p, contact: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Total Price ($)</label>
                <input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Deposit ($)</label>
                <input type="number" value={form.deposit} onChange={e => setForm(p => ({ ...p, deposit: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Rating (1–5)</label>
                <select value={form.rating} onChange={e => setForm(p => ({ ...p, rating: Number(e.target.value) }))}>
                  {[5,4,3,2,1].map(n => <option key={n} value={n}>{"★".repeat(n)}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row" style={{ alignItems: "center", gap: 24 }}>
              <label style={{ display: "flex", gap: 8, fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={form.depositPaid} onChange={e => setForm(p => ({ ...p, depositPaid: e.target.checked }))} />
                Deposit Paid
              </label>
              <label style={{ display: "flex", gap: 8, fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={form.contracted} onChange={e => setForm(p => ({ ...p, contracted: e.target.checked }))} />
                Contract Signed
              </label>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Important details, inclusions, etc." />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editing ? "Save" : "Add Vendor"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Checklist Tab ──────────────────────────────────────────────────────────────
function ChecklistTab({ checklist, setChecklist }) {
  const [filter, setFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const CATS = ["All", ...new Set(checklist.map(c => c.category))];
  const blank = { task: "", category: "Planning", priority: "medium", dueDate: "", assignedTo: "both", notes: "", done: false };
  const [form, setForm] = useState(blank);

  const ALL_CATS = ["Planning", "Venue", "Catering", "Attire", "Beauty", "Florals", "Stationery", "Music", "Photography", "Logistics", "Legal", "Honeymoon", "Rings", "Other"];

  const filtered = checklist.filter(c => {
    if (filter === "done" && !c.done) return false;
    if (filter === "todo" && c.done) return false;
    if (catFilter !== "All" && c.category !== catFilter) return false;
    return true;
  }).sort((a, b) => {
    const p = { high: 0, medium: 1, low: 2 };
    if (a.done !== b.done) return a.done ? 1 : -1;
    return p[a.priority] - p[b.priority];
  });

  const done = checklist.filter(c => c.done).length;
  const pct = checklist.length ? Math.round((done / checklist.length) * 100) : 0;

  function toggle(id) { setChecklist(prev => prev.map(c => c.id === id ? { ...c, done: !c.done } : c)); }
  function openAdd() { setForm(blank); setEditing(null); setShowModal(true); }
  function openEdit(c) { setForm({ ...c }); setEditing(c.id); setShowModal(true); }
  function save() {
    if (!form.task) return;
    if (editing) setChecklist(prev => prev.map(c => c.id === editing ? { ...form, id: editing } : c));
    else setChecklist(prev => [...prev, { ...form, id: uid() }]);
    setShowModal(false);
  }
  function remove(id) { setChecklist(prev => prev.filter(c => c.id !== id)); }

  return (
    <div>
      <div className="section-header">
        <div className="card-title" style={{ margin: 0 }}><Icon name="check" size={16} color="var(--gold)"/>Wedding Checklist</div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Task</button>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 14 }}>
          <strong>Overall Progress</strong>
          <span style={{ color: "var(--rose)", fontWeight: 600 }}>{pct}% complete</span>
        </div>
        <div className="progress-bar-track" style={{ height: 10 }}>
          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-dim)", marginTop: 8 }}>{done} of {checklist.length} tasks completed</div>
      </div>

      <div className="filter-bar">
        {["all","todo","done"].map(f => (
          <button key={f} className={`filter-chip ${filter===f?"active":""}`} onClick={()=>setFilter(f)}>
            {f==="all"?"All":f==="todo"?"To Do":"Completed"}
          </button>
        ))}
        <span style={{ color: "var(--border)", margin: "0 4px" }}>|</span>
        {["All", ...new Set(checklist.map(c => c.category))].map(c => (
          <button key={c} className={`filter-chip ${catFilter===c?"active":""}`} onClick={()=>setCatFilter(c)}>{c}</button>
        ))}
      </div>

      <div>
        {filtered.map(task => {
          const due = task.dueDate ? daysUntil(task.dueDate) : null;
          return (
            <div key={task.id} className={`checklist-item ${task.done ? "done" : ""}`}>
              <div className={`check-box ${task.done?"checked":""}`} onClick={() => toggle(task.id)}>
                {task.done && "✓"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 500, fontSize: 14, textDecoration: task.done ? "line-through" : "none" }}>{task.task}</span>
                  <span className="badge" style={{ background: "rgba(175,155,105,0.12)", color: "var(--ink-dim)", fontSize: 10 }}>{task.category}</span>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: PRIORITY_COLORS[task.priority], display: "inline-block" }} title={task.priority + " priority"} />
                  {task.assignedTo !== "both" && (
                    <span className="badge" style={{ background: task.assignedTo === "bride" ? "rgba(232,196,176,0.3)" : "rgba(122,155,122,0.2)", color: "var(--ink-dim)", fontSize: 10 }}>{task.assignedTo}</span>
                  )}
                </div>
                {task.notes && <div style={{ fontSize: 12, color: "var(--ink-dim)", marginTop: 3 }}>{task.notes}</div>}
                {due !== null && (
                  <div style={{ fontSize: 11, marginTop: 3, color: due < 0 ? "#8a4040" : due < 14 ? "var(--gold)" : "var(--ink-dim)" }}>
                    {due < 0 ? `⚠ ${Math.abs(due)} days overdue` : due === 0 ? "Due today!" : `Due in ${due} days · ${task.dueDate}`}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button className="btn-icon" onClick={() => openEdit(task)}>✏️</button>
                <button className="btn-icon" onClick={() => remove(task.id)}><Icon name="trash" size={13} color="currentColor"/></button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="empty-state card"><Icon name="check" size={36} color="var(--ink-faint)"/><p>No tasks here</p></div>}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">{editing ? "Edit Task" : "Add Task"}</div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Task *</label>
              <input value={form.task} onChange={e => setForm(p => ({ ...p, task: e.target.value }))} placeholder="What needs to be done?" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  {ALL_CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Priority</label>
                <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
                  <option value="high">🔴 High</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="low">🔵 Low</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Due Date</label>
                <input type="date" value={form.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Assigned To</label>
                <select value={form.assignedTo} onChange={e => setForm(p => ({ ...p, assignedTo: e.target.value }))}>
                  <option value="both">Both</option>
                  <option value="bride">Bride</option>
                  <option value="groom">Groom</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional details…" />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editing ? "Save" : "Add Task"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Registry Tab ─────────────────────────────────────────────────────────────
function RegistryTab({ registry, setRegistry }) {
  const [catFilter, setCatFilter] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const CATS = ["Kitchen","Bedroom","Bathroom","Living Room","Outdoor","Entertainment","Travel","Experience","Other"];
  const EMOJIS = ["🎁","🍳","🛏","🛁","🛋","🌿","✈️","🕯","📷","🎵","🍷","🌸","⭐","💎"];
  const blank = { item:"", category:"Kitchen", store:"", price:"", url:"", imageUrl:"", emoji:"🎁", priority:"medium", purchased:false, purchasedBy:"", notes:"" };
  const [form, setForm] = useState(blank);

  const allCats = ["All", ...new Set(registry.map(r => r.category))];
  const filtered = catFilter === "All" ? registry : registry.filter(r => r.category === catFilter);
  const totalValue = registry.reduce((s,r) => s + Number(r.price||0), 0);
  const purchasedValue = registry.filter(r => r.purchased).reduce((s,r) => s + Number(r.price||0), 0);
  const purchasedCount = registry.filter(r => r.purchased).length;

  function openAdd() { setForm(blank); setEditing(null); setShowModal(true); }
  function openEdit(r) { setForm({...r}); setEditing(r.id); setShowModal(true); }
  function save() {
    if (!form.item) return;
    const item = { ...form, price: Number(form.price), id: editing || uid() };
    if (editing) setRegistry(prev => prev.map(r => r.id === editing ? item : r));
    else setRegistry(prev => [...prev, item]);
    setShowModal(false);
  }
  function toggle(id) { setRegistry(prev => prev.map(r => r.id === id ? {...r, purchased: !r.purchased} : r)); }
  function remove(id) { setRegistry(prev => prev.filter(r => r.id !== id)); }

  return (
    <div>
      <div className="section-header">
        <div className="card-title" style={{margin:0}}>
          <Icon name="gift" size={16} color="var(--gold)"/> Gift Registry
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <Icon name="plus" size={13} color="currentColor"/> Add Item
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{gridTemplateColumns:"repeat(4,1fr)",marginBottom:20}}>
        <div className="stat-card"><div className="stat-num" style={{fontSize:26}}>{registry.length}</div><div className="stat-label">Items</div></div>
        <div className="stat-card"><div className="stat-num" style={{fontSize:26}}>{currency(totalValue)}</div><div className="stat-label">Total Value</div></div>
        <div className="stat-card"><div className="stat-num" style={{fontSize:26,color:"var(--sage)"}}>{purchasedCount}</div><div className="stat-label">Purchased</div></div>
        <div className="stat-card"><div className="stat-num" style={{fontSize:26,color:"var(--gold)"}}>{currency(purchasedValue)}</div><div className="stat-label">Gifted</div></div>
      </div>

      {/* Zola fund pinned card */}
      <div className="card" style={{marginBottom:14,padding:16,display:"flex",alignItems:"center",gap:14,borderColor:"rgba(200,169,110,0.3)"}}>
        <div style={{flexShrink:0,width:48,height:48,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <svg viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width:52,height:52}}>
            <path d="M30 80 Q20 60 25 40 Q30 25 40 20" stroke="var(--sage)" strokeWidth="0.9" strokeLinecap="round" fill="none" opacity="0.6"/>
            <path d="M25 60 Q12 55 8 44" stroke="var(--sage)" strokeWidth="0.8" strokeLinecap="round" fill="none" opacity="0.5"/>
            <ellipse cx="7" cy="42" rx="9" ry="4.5" transform="rotate(-25 7 42)" fill="none" stroke="var(--sage)" strokeWidth="0.7" opacity="0.55"/>
            <ellipse cx="38" cy="16" rx="9" ry="4" transform="rotate(10 38 16)" fill="none" stroke="var(--sage)" strokeWidth="0.7" opacity="0.5"/>
            <path d="M130 80 Q140 60 135 40 Q130 25 120 20" stroke="var(--sage)" strokeWidth="0.9" strokeLinecap="round" fill="none" opacity="0.6"/>
            <path d="M135 60 Q148 55 152 44" stroke="var(--sage)" strokeWidth="0.8" strokeLinecap="round" fill="none" opacity="0.5"/>
            <ellipse cx="153" cy="42" rx="9" ry="4.5" transform="rotate(25 153 42)" fill="none" stroke="var(--sage)" strokeWidth="0.7" opacity="0.55"/>
            <ellipse cx="122" cy="16" rx="9" ry="4" transform="rotate(-10 122 16)" fill="none" stroke="var(--sage)" strokeWidth="0.7" opacity="0.5"/>
            <path d="M45 130 Q80 148 115 130" stroke="var(--sage)" strokeWidth="0.8" strokeLinecap="round" fill="none" opacity="0.45"/>
            <circle cx="80" cy="146" r="2.5" fill="var(--gold)" opacity="0.5"/>
            <circle cx="66" cy="78" r="22" stroke="var(--gold)" strokeWidth="1.8" fill="none"/>
            <circle cx="94" cy="78" r="22" stroke="var(--gold)" strokeWidth="1.8" fill="none"/>
            <path d="M80 44 L83 50 L80 53 L77 50 Z" stroke="var(--gold)" strokeWidth="0.8" fill="none" opacity="0.6"/>
            <circle cx="42" cy="58" r="1.2" fill="var(--gold)" opacity="0.35"/>
            <circle cx="118" cy="58" r="1.2" fill="var(--gold)" opacity="0.35"/>
          </svg>
        </div>
        <div style={{flex:1}}>
          <div style={{fontWeight:600,fontSize:14,color:"var(--rose-light)",marginBottom:2}}>Honeymoon &amp; Future Home Fund</div>
          <div style={{fontSize:12,color:"var(--ink-dim)"}}>Zola · Pinned — always shown first on the website</div>
        </div>
        <a href="https://www.zola.com/registry/khalibandzoe" target="_blank" rel="noopener"
          style={{fontSize:11,color:"var(--gold)",letterSpacing:1,textTransform:"uppercase",textDecoration:"none",border:"1px solid rgba(200,169,110,0.3)",padding:"5px 12px",borderRadius:6,whiteSpace:"nowrap"}}>
          View on Zola ↗
        </a>
      </div>

      {/* Filter chips */}
      <div className="filter-bar">
        {allCats.map(c => (
          <button key={c} className={`filter-chip ${catFilter===c?"active":""}`} onClick={() => setCatFilter(c)}>{c}</button>
        ))}
      </div>

      {/* Items */}
      {filtered.map(item => (
        <div key={item.id} className={`registry-item ${item.purchased?"purchased":""}`}>
          {/* Thumbnail */}
          <div style={{width:46,height:46,borderRadius:6,background:"var(--forest-lift)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",border:"1px solid var(--border)"}}>
            {item.imageUrl
              ? <img src={item.imageUrl} alt={item.item} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              : <span style={{fontSize:20}}>{item.emoji||"🎁"}</span>}
          </div>
          {/* Purchased check */}
          <div className={`registry-check ${item.purchased?"checked":""}`} onClick={() => toggle(item.id)}>
            {item.purchased && <Icon name="sparkle" size={10} color="#fff"/>}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{fontWeight:600,fontSize:13,color:"var(--ink)"}}>{item.item}</span>
              <span className="badge" style={{background:"rgba(175,155,105,0.1)",color:"var(--ink-dim)",fontSize:9}}>{item.category}</span>
              <span style={{width:7,height:7,borderRadius:"50%",background:PRIORITY_COLORS[item.priority],display:"inline-block",flexShrink:0}}/>
            </div>
            <div style={{fontSize:12,color:"var(--ink-dim)",marginTop:2}}>
              {item.store}
              {item.url && <a href={item.url} target="_blank" rel="noopener" style={{color:"var(--sage)",marginLeft:8,fontSize:11}}>View ↗</a>}
              {item.purchased && item.purchasedBy && <span style={{color:"var(--sage)",marginLeft:8}}>· Gifted by {item.purchasedBy}</span>}
            </div>
            {item.notes && <div style={{fontSize:11,color:"var(--ink-faint)",fontStyle:"italic",marginTop:2}}>{item.notes}</div>}
          </div>
          <div style={{fontWeight:600,fontSize:14,color:"var(--gold)",minWidth:60,textAlign:"right",flexShrink:0}}>{currency(item.price)}</div>
          <div style={{display:"flex",gap:4,flexShrink:0}}>
            <button className="btn-icon" onClick={() => openEdit(item)}><Icon name="edit" size={13} color="currentColor"/></button>
            <button className="btn-icon" onClick={() => remove(item.id)}><Icon name="trash" size={13} color="currentColor"/></button>
          </div>
        </div>
      ))}
      {filtered.length === 0 && (
        <div className="card" style={{textAlign:"center",padding:"40px 24px"}}>
          <Icon name="gift" size={36} color="var(--ink-faint)"/>
          <p style={{color:"var(--ink-faint)",fontSize:13,marginTop:12}}>
            {registry.length === 0 ? "No items yet — add your first gift below" : "No items in this category"}
          </p>
          {registry.length === 0 && <button className="btn btn-primary" style={{marginTop:16}} onClick={openAdd}><Icon name="plus" size={13}/>Add First Item</button>}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">
              <Icon name="gift" size={16} color="var(--rose)"/>
              {editing ? "Edit Item" : "Add Registry Item"}
            </div>
            <div className="form-row">
              <div className="form-group" style={{flex:2}}>
                <label>Item Name *</label>
                <input value={form.item} onChange={e => setForm(p=>({...p,item:e.target.value}))} placeholder="e.g. KitchenAid Stand Mixer"/>
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm(p=>({...p,category:e.target.value}))}>
                  {CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Store / Retailer</label>
                <input value={form.store} onChange={e => setForm(p=>({...p,store:e.target.value}))} placeholder="Williams Sonoma"/>
              </div>
              <div className="form-group">
                <label>Price ($)</label>
                <input type="number" value={form.price} onChange={e => setForm(p=>({...p,price:e.target.value}))}/>
              </div>
              <div className="form-group">
                <label>Priority</label>
                <select value={form.priority} onChange={e => setForm(p=>({...p,priority:e.target.value}))}>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
            <div className="form-group" style={{marginBottom:14}}>
              <label>Product URL (guests click this on the site)</label>
              <input value={form.url} onChange={e => setForm(p=>({...p,url:e.target.value}))} placeholder="https://…"/>
            </div>
            <div className="form-group" style={{marginBottom:14}}>
              <label>Image URL (optional — shown on site)</label>
              <input value={form.imageUrl} onChange={e => setForm(p=>({...p,imageUrl:e.target.value}))} placeholder="https://… (product photo URL)"/>
            </div>
            <div className="form-group" style={{marginBottom:14}}>
              <label>Emoji (shown if no image)</label>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:4}}>
                {EMOJIS.map(e => (
                  <button key={e} onClick={() => setForm(p=>({...p,emoji:e}))}
                    style={{fontSize:20,padding:"4px 8px",borderRadius:6,border:`1px solid ${form.emoji===e?"var(--border-glow)":"var(--border)"}`,background:form.emoji===e?"rgba(200,169,110,0.12)":"transparent",cursor:"pointer",transition:"all 0.15s"}}>
                    {e}
                  </button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{display:"flex",gap:8,fontSize:12,cursor:"pointer",color:"var(--ink-dim)",alignItems:"center"}}>
                <input type="checkbox" checked={form.purchased} onChange={e => setForm(p=>({...p,purchased:e.target.checked}))}/>
                Mark as purchased
              </label>
            </div>
            {form.purchased && (
              <div className="form-group" style={{marginBottom:14}}>
                <label>Purchased By</label>
                <input value={form.purchasedBy} onChange={e => setForm(p=>({...p,purchasedBy:e.target.value}))} placeholder="Gifter's name"/>
              </div>
            )}
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(p=>({...p,notes:e.target.value}))} placeholder="Color preference, size, etc."/>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editing ? "Save Changes" : "Add Item"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Venues Tab ─────────────────────────────────────────────────────────────────
function VenueTab({ venues, setVenues }) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { name: "", location: "", capacity: "", price: "", style: "Garden", status: "considering", rating: 4, contact: "", phone: "", visitDate: "", notes: "", pros: "", cons: "" };
  const [form, setForm] = useState(blank);
  const STYLES = ["Garden", "Ballroom", "Barn", "Beach", "Rooftop", "Historic", "Industrial", "Restaurant", "Museum", "Vineyard", "Resort", "Other"];
  const STATUSES = { considering: { label: "Considering", color: "var(--gold)", bg: "rgba(240,192,96,0.12)" }, visited: { label: "Visited", color: "#6cb8f0", bg: "rgba(108,184,240,0.12)" }, booked: { label: "Booked ✓", color: "#6cc87a", bg: "rgba(108,200,122,0.12)" }, rejected: { label: "Not Chosen", color: "#ccc", bg: "rgba(200,200,200,0.12)" } };

  function openAdd() { setForm(blank); setEditing(null); setShowModal(true); }
  function openEdit(v) { setForm({ ...v }); setEditing(v.id); setShowModal(true); }
  function save() {
    if (!form.name) return;
    const v = { ...form, capacity: Number(form.capacity), price: Number(form.price), id: editing || uid() };
    if (editing) setVenues(prev => prev.map(x => x.id === editing ? v : x));
    else setVenues(prev => [...prev, v]);
    setShowModal(false);
  }
  function remove(id) { setVenues(prev => prev.filter(v => v.id !== id)); }

  return (
    <div>
      <div className="section-header">
        <div className="card-title" style={{ margin: 0 }}><Icon name="venue" size={16} color="var(--gold)"/>Venues ({venues.length})</div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Venue</button>
      </div>
      {venues.map(v => {
        const st = STATUSES[v.status] || STATUSES.considering;
        return (
          <div key={v.id} className="venue-card">
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 18, fontFamily: "Cormorant Garamond", marginBottom: 2 }}>{v.name}</div>
                    <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>📍 {v.location}</div>
                  </div>
                  <span className="venue-status badge" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                </div>
                <div className="stars">{"★".repeat(v.rating)}{"☆".repeat(5 - v.rating)}</div>
                <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 13 }}><span style={{ color: "var(--ink-dim)" }}>Style:</span> {v.style}</div>
                  <div style={{ fontSize: 13 }}><span style={{ color: "var(--ink-dim)" }}>Capacity:</span> {v.capacity}</div>
                  <div style={{ fontSize: 13 }}><span style={{ color: "var(--ink-dim)" }}>Price:</span> <strong>{currency(v.price)}</strong></div>
                </div>
                {v.visitDate && <div style={{ fontSize: 12, color: "var(--ink-dim)", marginTop: 6 }}>Visited: {v.visitDate}</div>}
                {v.notes && <div style={{ fontSize: 13, color: "var(--ink)", marginTop: 8, lineHeight: 1.5 }}>{v.notes}</div>}
                <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
                  {v.pros && <div style={{ fontSize: 12 }}><span style={{ color: "#6cc87a", fontWeight: 600 }}>✓ Pros:</span> {v.pros}</div>}
                  {v.cons && <div style={{ fontSize: 12 }}><span style={{ color: "#8a4040", fontWeight: 600 }}>✗ Cons:</span> {v.cons}</div>}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, justifyContent: "flex-end" }}>
                {v.contact && <div style={{ fontSize: 12, color: "var(--ink-dim)" }}>📞 {v.contact} {v.phone && `· ${v.phone}`}</div>}
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(v)}>Edit</button>
                  <button className="btn btn-secondary btn-sm" style={{ color: "#8a4040" }} onClick={() => remove(v.id)}>Remove</button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {venues.length === 0 && <div className="empty-state card"><Icon name="venue" size={36} color="var(--ink-faint)"/><p>Add venues you're considering</p></div>}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">{editing ? "Edit Venue" : "Add Venue"}</div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>Venue Name *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Style</label>
                <select value={form.style} onChange={e => setForm(p => ({ ...p, style: e.target.value }))}>
                  {STYLES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>Location</label>
                <input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="City, State" />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                  {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Capacity</label>
                <input type="number" value={form.capacity} onChange={e => setForm(p => ({ ...p, capacity: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Price ($)</label>
                <input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Rating</label>
                <select value={form.rating} onChange={e => setForm(p => ({ ...p, rating: Number(e.target.value) }))}>
                  {[5,4,3,2,1].map(n => <option key={n} value={n}>{"★".repeat(n)}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Contact</label>
                <input value={form.contact} onChange={e => setForm(p => ({ ...p, contact: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Visit Date</label>
                <input type="date" value={form.visitDate} onChange={e => setForm(p => ({ ...p, visitDate: e.target.value }))} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Pros</label>
                <input value={form.pros} onChange={e => setForm(p => ({ ...p, pros: e.target.value }))} placeholder="What you love…" />
              </div>
              <div className="form-group">
                <label>Cons</label>
                <input value={form.cons} onChange={e => setForm(p => ({ ...p, cons: e.target.value }))} placeholder="Concerns…" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editing ? "Save" : "Add Venue"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Budget Tab ─────────────────────────────────────────────────────────────────
function BudgetTab({ budget, setBudget }) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { name: "", budgeted: "", spent: "" };
  const [form, setForm] = useState(blank);
  const [totalForm, setTotalForm] = useState(String(budget.total));
  const [editTotal, setEditTotal] = useState(false);

  const totalBudgeted = budget.categories.reduce((s, c) => s + c.budgeted, 0);
  const totalSpent = budget.categories.reduce((s, c) => s + c.spent, 0);
  const remaining = budget.total - totalSpent;

  function save() {
    if (!form.name) return;
    const cat = { ...form, budgeted: Number(form.budgeted), spent: Number(form.spent), id: editing || uid() };
    if (editing) setBudget(prev => ({ ...prev, categories: prev.categories.map(c => c.id === editing ? cat : c) }));
    else setBudget(prev => ({ ...prev, categories: [...prev.categories, cat] }));
    setShowModal(false);
  }
  function remove(id) { setBudget(prev => ({ ...prev, categories: prev.categories.filter(c => c.id !== id) })); }
  function openEdit(c) { setForm({ ...c }); setEditing(c.id); setShowModal(true); }
  function openAdd() { setForm(blank); setEditing(null); setShowModal(true); }

  return (
    <div>
      <div className="section-header">
        <div className="card-title" style={{ margin: 0 }}><Icon name="budget" size={16} color="var(--gold)"/>Budget Tracker</div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Category</button>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", marginBottom: 24 }}>
        <div className="stat-card">
          <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
            <div className="stat-num" style={{ fontSize: 26 }}>{currency(budget.total)}</div>
            <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "var(--ink-dim)" }} onClick={() => setEditTotal(true)}>✏️</button>
          </div>
          <div className="stat-label">Total Budget</div>
        </div>
        <div className="stat-card">
          <div className="stat-num" style={{ fontSize: 26 }}>{currency(totalBudgeted)}</div>
          <div className="stat-label">Allocated</div>
        </div>
        <div className="stat-card">
          <div className="stat-num" style={{ fontSize: 26, color: totalSpent > budget.total ? "#8a4040" : "var(--rose-dim)" }}>{currency(totalSpent)}</div>
          <div className="stat-label">Spent</div>
        </div>
        <div className="stat-card">
          <div className="stat-num" style={{ fontSize: 26, color: remaining < 0 ? "#8a4040" : "var(--sage)" }}>{currency(remaining)}</div>
          <div className="stat-label">Remaining</div>
        </div>
      </div>

      {editTotal && (
        <div className="card" style={{ marginBottom: 20, display: "flex", gap: 12, alignItems: "center" }}>
          <div className="form-group" style={{ margin: 0, flex: 1 }}>
            <label>Total Budget ($)</label>
            <input type="number" value={totalForm} onChange={e => setTotalForm(e.target.value)} />
          </div>
          <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={() => { setBudget(p => ({ ...p, total: Number(totalForm) })); setEditTotal(false); }}>Save</button>
          <button className="btn btn-secondary" style={{ marginTop: 20 }} onClick={() => setEditTotal(false)}>Cancel</button>
        </div>
      )}

      <div className="card">
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 14 }}>
            <strong>Overall Spend</strong>
            <span style={{ color: totalSpent > budget.total ? "#8a4040" : "var(--sage)" }}>
              {Math.round((totalSpent / budget.total) * 100)}%
            </span>
          </div>
          <div className="progress-bar-track" style={{ height: 12 }}>
            <div className="progress-bar-fill" style={{
              width: `${Math.min(100, (totalSpent / budget.total) * 100)}%`,
              background: totalSpent > budget.total ? "#8a4040" : "linear-gradient(90deg, var(--rose), var(--rose-dim))"
            }} />
          </div>
        </div>

        {budget.categories.map(cat => {
          const pct = cat.budgeted ? (cat.spent / cat.budgeted) * 100 : 0;
          const over = cat.spent > cat.budgeted;
          return (
            <div key={cat.id} className="budget-bar-wrap" style={{ marginBottom: 18 }}>
              <div className="budget-bar-header" style={{ marginBottom: 6 }}>
                <span style={{ fontWeight: 500, fontSize: 14 }}>{cat.name}</span>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>
                    {currency(cat.spent)} <span style={{ color: "var(--border)" }}>of</span> {currency(cat.budgeted)}
                  </span>
                  {over && <span className="badge" style={{ background: "rgba(224,96,96,0.12)", color: "#8a4040", fontSize: 10 }}>Over budget</span>}
                  <button className="btn-icon" style={{ fontSize: 11, padding: "3px 7px" }} onClick={() => openEdit(cat)}>✏️</button>
                  <button className="btn-icon" onClick={() => remove(cat.id)}><Icon name="trash" size={13} color="currentColor"/></button>
                </div>
              </div>
              <div className="budget-bar-track" style={{ height: 10 }}>
                <div className="budget-bar-fill" style={{
                  width: `${Math.min(100, pct)}%`,
                  background: over ? "#8a4040" : pct > 80 ? "var(--gold)" : "linear-gradient(90deg, var(--sage), var(--sage-dim))"
                }} />
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 4 }}>
                {currency(Math.max(0, cat.budgeted - cat.spent))} remaining
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-title">{editing ? "Edit Category" : "Add Budget Category"}</div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Category Name</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Photography" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Budgeted ($)</label>
                <input type="number" value={form.budgeted} onChange={e => setForm(p => ({ ...p, budgeted: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Spent ($)</label>
                <input type="number" value={form.spent} onChange={e => setForm(p => ({ ...p, spent: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editing ? "Save" : "Add"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Timeline Tab ───────────────────────────────────────────────────────────────
function TimelineTab({ timeline, setTimeline }) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { time: "", event: "", duration: 30, category: "reception", notes: "" };
  const [form, setForm] = useState(blank);

  const sorted = [...timeline].sort((a, b) => a.time.localeCompare(b.time));

  function openAdd() { setForm(blank); setEditing(null); setShowModal(true); }
  function openEdit(t) { setForm({ ...t }); setEditing(t.id); setShowModal(true); }
  function save() {
    if (!form.event || !form.time) return;
    const item = { ...form, duration: Number(form.duration), id: editing || uid() };
    if (editing) setTimeline(prev => prev.map(t => t.id === editing ? item : t));
    else setTimeline(prev => [...prev, item]);
    setShowModal(false);
  }
  function remove(id) { setTimeline(prev => prev.filter(t => t.id !== id)); }

  return (
    <div>
      <div className="section-header">
        <div className="card-title" style={{ margin: 0 }}><span className="icon">⏰</span>Day-of Timeline</div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Event</button>
      </div>
      <div className="card">
        {sorted.map((item, i) => {
          const color = CAT_COLORS[item.category] || "#ccc";
          return (
            <div key={item.id} className="timeline-item" style={{ position: "relative" }}>
              {i < sorted.length - 1 && (
                <div style={{ position: "absolute", left: 81, top: 28, width: 2, height: "calc(100% - 14px)", background: "rgba(175,155,105,0.08)", zIndex: 0 }} />
              )}
              <div className="timeline-time">{item.time}</div>
              <div className="timeline-dot" style={{ background: color, borderColor: color, position: "relative", zIndex: 1 }} />
              <div className="timeline-content">
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div className="timeline-event">{item.event}</div>
                  <span className="badge" style={{ background: `${color}33`, color: "var(--ink)", fontSize: 10, textTransform: "capitalize" }}>{item.category}</span>
                  <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>{item.duration} min</span>
                </div>
                {item.notes && <div className="timeline-note">{item.notes}</div>}
              </div>
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                <button className="btn-icon" style={{ fontSize: 11 }} onClick={() => openEdit(item)}>✏️</button>
                <button className="btn-icon" onClick={() => remove(item.id)}><Icon name="trash" size={13} color="currentColor"/></button>
              </div>
            </div>
          );
        })}
        {timeline.length === 0 && <div className="empty-state"><div className="e-icon">⏰</div><p>Build your day-of timeline</p></div>}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-title">{editing ? "Edit Event" : "Add Timeline Event"}</div>
            <div className="form-row">
              <div className="form-group">
                <label>Time</label>
                <input type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} />
              </div>
              <div className="form-group" style={{ flex: 2 }}>
                <label>Event Name *</label>
                <input value={form.event} onChange={e => setForm(p => ({ ...p, event: e.target.value }))} placeholder="e.g. First Dance" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Duration (min)</label>
                <input type="number" value={form.duration} onChange={e => setForm(p => ({ ...p, duration: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  <option value="ceremony">Ceremony</option>
                  <option value="reception">Reception</option>
                  <option value="dining">Dining</option>
                  <option value="logistics">Logistics</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Song, instructions, cue notes…" />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editing ? "Save" : "Add Event"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Settings / Info Modal ──────────────────────────────────────────────────────
function InfoModal({ info, setInfo, onClose }) {
  const [form, setForm] = useState({ ...info });
  function save() { setInfo(form); onClose(); }
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">✨ Wedding Details</div>
        <div className="form-row">
          <div className="form-group">
            <label>Partner 1 Name</label>
            <input value={form.partner1} onChange={e => setForm(p => ({ ...p, partner1: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Partner 2 Name</label>
            <input value={form.partner2} onChange={e => setForm(p => ({ ...p, partner2: e.target.value }))} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Wedding Date</label>
            <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Theme / Style</label>
            <input value={form.theme} onChange={e => setForm(p => ({ ...p, theme: e.target.value }))} placeholder="e.g. Garden Romance" />
          </div>
        </div>
        <div className="form-group">
          <label>Venue</label>
          <input value={form.venue} onChange={e => setForm(p => ({ ...p, venue: e.target.value }))} placeholder="Venue name" />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── Main App ───────────────────────────────────────────────────────────────────

// ── RSVP Submissions Tab ──────────────────────────────────────────────────────
function RsvpTab({ rsvps, setRsvps }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const filtered = rsvps.filter(r => {
    if (filter !== "all" && r.attending !== filter) return false;
    if (search && !`${r.firstname} ${r.lastname}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));

  const confirmed = rsvps.filter(r => r.attending === "yes");
  const declined  = rsvps.filter(r => r.attending === "no");
  const totalGuests = confirmed.reduce((s, r) => s + Number(r.guests || 1), 0);

  function remove(id) { setRsvps(prev => prev.filter(r => r.id !== id)); }

  return (
    <div>
      <div className="sec-header">
        <div className="card-title" style={{ margin: 0 }}>
          <Icon name="scroll" size={16} color="var(--gold)"/> RSVP Submissions
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: "repeat(4,1fr)", marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-num">{rsvps.length}</div><div className="stat-label">Total RSVPs</div></div>
        <div className="stat-card"><div className="stat-num" style={{ color: "var(--sage)" }}>{confirmed.length}</div><div className="stat-label">Attending</div></div>
        <div className="stat-card"><div className="stat-num" style={{ color: "#c04040" }}>{declined.length}</div><div className="stat-label">Declined</div></div>
        <div className="stat-card"><div className="stat-num">{totalGuests}</div><div className="stat-label">Total Guests</div></div>
      </div>

      {rsvps.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "48px 24px" }}>
          <Icon name="scroll" size={40} color="var(--ink-faint)"/>
          <p style={{ color: "var(--ink-faint)", fontSize: 14, marginTop: 12, fontStyle: "italic" }}>
            No RSVPs yet — submissions from the wedding website will appear here automatically.
          </p>
        </div>
      )}

      {rsvps.length > 0 && (
        <>
          {/* Search + filter */}
          <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <input
              style={{ flex: 1, minWidth: 200, padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--forest-lift)", color: "var(--ink)", fontFamily: "Raleway", fontSize: 13, outline: "none" }}
              placeholder="Search by name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-bar">
            {[["all","All",rsvps.length],["yes","Attending",confirmed.length],["no","Declined",declined.length]].map(([val,label,count]) => (
              <button key={val} className={`filter-chip ${filter===val?"active":""}`} onClick={() => setFilter(val)}>
                {label} ({count})
              </button>
            ))}
          </div>

          {/* List */}
          {filtered.map(r => (
            <div key={r.id} style={{
              background: "var(--forest-card)", border: "1px solid var(--border)",
              borderRadius: "var(--radius)", marginBottom: 10, overflow: "hidden",
              transition: "border-color 0.15s"
            }}>
              {/* Row header */}
              <div
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", cursor: "pointer" }}
                onClick={() => setExpanded(expanded === r.id ? null : r.id)}
              >
                <div style={{
                  width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                  background: r.attending === "yes" ? "var(--sage)" : "#c04040"
                }}/>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "var(--rose-light)" }}>
                    {r.firstname} {r.lastname}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 2 }}>
                    {r.email}{r.submittedAt ? ` · ${new Date(r.submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                  </div>
                </div>
                <span className="badge" style={{
                  background: r.attending === "yes" ? "rgba(122,170,128,0.15)" : "rgba(192,64,64,0.15)",
                  color: r.attending === "yes" ? "var(--sage)" : "#c04040", fontSize: 9
                }}>
                  {r.attending === "yes" ? "✦ Attending" : "✗ Declined"}
                </span>
                {r.attending === "yes" && (
                  <span className="badge" style={{ background: "rgba(200,169,110,0.12)", color: "var(--gold)", fontSize: 9 }}>
                    {r.guests} {Number(r.guests) === 1 ? "guest" : "guests"}
                  </span>
                )}
                <span style={{ color: "var(--ink-faint)", fontSize: 12, marginLeft: 4 }}>
                  {expanded === r.id ? "▲" : "▼"}
                </span>
              </div>

              {/* Expanded detail */}
              {expanded === r.id && (
                <div style={{ borderTop: "1px solid var(--border)", padding: "16px 18px", background: "var(--forest-lift)" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 14 }}>
                    {r.attending === "yes" && r.meal && (
                      <div>
                        <div style={{ fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: "var(--ink-faint)", marginBottom: 4 }}>Meal Selection</div>
                        <div style={{ fontSize: 13, color: "var(--ink)" }}>{r.meal}</div>
                      </div>
                    )}
                    {r.dietary && r.dietary !== "None" && (
                      <div>
                        <div style={{ fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: "var(--ink-faint)", marginBottom: 4 }}>Dietary Notes</div>
                        <div style={{ fontSize: 13, color: "var(--ink)" }}>{r.dietary}</div>
                      </div>
                    )}
                    {r.song && r.song !== "—" && (
                      <div>
                        <div style={{ fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: "var(--ink-faint)", marginBottom: 4 }}>Song Request</div>
                        <div style={{ fontSize: 13, color: "var(--gold)" }}>♪ {r.song}</div>
                      </div>
                    )}
                    {r.message && r.message !== "—" && (
                      <div style={{ gridColumn: "1/-1" }}>
                        <div style={{ fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: "var(--ink-faint)", marginBottom: 4 }}>Message</div>
                        <div style={{ fontSize: 13, color: "var(--ink)", fontStyle: "italic", lineHeight: 1.6 }}>"{r.message}"</div>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
                    <button className="btn-icon" onClick={() => remove(r.id)} title="Remove submission">
                      <Icon name="trash" size={13} color="currentColor"/>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="empty-state card">
              <Icon name="scroll" size={36} color="var(--ink-faint)"/>
              <p>No RSVPs match this filter</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const TAB_GROUPS = [
  {
    id: "overview", label: "Overview", icon: "home",
    tabs: [
      { id: "overview", label: "Overview", icon: "home" },
    ]
  },
  {
    id: "guests", label: "Guests", icon: "guests",
    tabs: [
      { id: "guests",        label: "Guest List",   icon: "guests"  },
      { id: "seating",       label: "Seating",      icon: "seat"    },
      { id: "invitations",   label: "Invitations",  icon: "feather" },
      { id: "rsvps",         label: "RSVPs",        icon: "scroll"  },
      { id: "meals",         label: "Meals",        icon: "leaf"    },
      { id: "songs",         label: "Songs",        icon: "time"    },
      { id: "accommodations",label: "Stays",        icon: "venue"   },
    ]
  },
  {
    id: "planning", label: "Planning", icon: "check",
    tabs: [
      { id: "checklist", label: "Checklist", icon: "check"   },
      { id: "vendors",   label: "Vendors",   icon: "vendor"  },
      { id: "budget",    label: "Budget",    icon: "budget"  },
      { id: "payments",  label: "Payments",  icon: "budget"  },
      { id: "venues",    label: "Venues",    icon: "venue"   },
      { id: "contacts",  label: "Contacts",  icon: "guests"  },
    ]
  },
  {
    id: "celebration", label: "Celebration", icon: "ring",
    tabs: [
      { id: "timeline",     label: "Timeline",      icon: "time"    },
      { id: "ceremony",     label: "Ceremony",      icon: "ring"    },
      { id: "weddingparty", label: "Wedding Party", icon: "dove"    },
      { id: "decor",        label: "Decor",         icon: "flower"  },
    ]
  },
  {
    id: "registry", label: "Registry", icon: "gift",
    tabs: [
      { id: "registry", label: "Registry", icon: "gift" },
      { id: "gifts",    label: "Gifts",    icon: "gift" },
    ]
  },
  {
    id: "honeymoon", label: "Honeymoon", icon: "moon",
    tabs: [
      { id: "honeymoon", label: "Honeymoon", icon: "moon" },
    ]
  },
  {
    id: "notes", label: "Notes", icon: "scroll",
    tabs: [
      { id: "notes", label: "Notes",  icon: "scroll"   },
      { id: "dayof", label: "Day Of", icon: "sparkle"  },
    ]
  },
];

// Flat list for render lookup
const TABS = TAB_GROUPS.flatMap(g => g.tabs);


// ── App Root ──────────────────────────────────────────────────────────────────
// ── Dashboard Password Gate ──────────────────────────────────────────────────
const GATE_KEY = "kz_dashboard_auth";
const GATE_PASSWORD = "electric"; // 🔑 Change this to whatever you want

function DashboardGate({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  function tryUnlock() {
    if (pw.trim() === GATE_PASSWORD) {
      localStorage.setItem(GATE_KEY, "1");
      onUnlock();
    } else {
      setError(true);
      setShake(true);
      setPw("");
      setTimeout(() => setShake(false), 400);
    }
  }

  return (
    <div style={{ position:"fixed", inset:0, background:"var(--forest)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999, fontFamily:"Raleway, sans-serif" }}>
      <style>{CSS}</style>
      <style>{`
        @keyframes gateShake {
          0%,100%{transform:translateX(0)}
          20%{transform:translateX(-8px)}
          40%{transform:translateX(8px)}
          60%{transform:translateX(-5px)}
          80%{transform:translateX(5px)}
        }
        .gate-shake { animation: gateShake 0.4s ease; }
      `}</style>
      <div style={{ textAlign:"center", padding:"3rem 2rem", maxWidth:400, width:"100%" }}>
        <FairyDust/>
        {/* Crest */}
        <div style={{ width:72, height:72, borderRadius:"50%", border:"1px solid rgba(200,169,110,0.3)", background:"rgba(200,169,110,0.07)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 24px" }}>
          <Icon name="sparkle" size={28} color="var(--gold)"/>
        </div>
        {/* Title */}
        <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:32, color:"var(--rose-light)", marginBottom:6, fontStyle:"italic" }}>
          Khalib &amp; Zoë
        </div>
        <div style={{ fontSize:10, letterSpacing:"0.28em", textTransform:"uppercase", color:"var(--ink-faint)", marginBottom:32 }}>
          Wedding Dashboard
        </div>
        {/* Input */}
        <div className={shake ? "gate-shake" : ""}>
          <label style={{ display:"block", fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"var(--ink-dim)", marginBottom:8 }}>
            Enter password to continue
          </label>
          <input
            type="password"
            value={pw}
            onChange={e => { setPw(e.target.value); setError(false); }}
            onKeyDown={e => e.key === "Enter" && tryUnlock()}
            placeholder="••••••••"
            autoFocus
            style={{ width:"100%", padding:"12px 16px", borderRadius:4, border:`1px solid ${error ? "#c04040" : "rgba(175,155,105,0.3)"}`, background:"var(--forest-lift)", color:"var(--ink)", fontFamily:"Cormorant Garamond, serif", fontSize:18, textAlign:"center", letterSpacing:"0.15em", outline:"none", transition:"border-color 0.2s", marginBottom:12 }}
          />
          {error && (
            <div style={{ fontSize:11, color:"#c06060", letterSpacing:"0.08em", marginBottom:12 }}>
              Incorrect password — please try again.
            </div>
          )}
          <button
            onClick={tryUnlock}
            style={{ width:"100%", padding:"12px", background:"linear-gradient(135deg, #7a4535, #5a3025)", border:"none", borderRadius:4, color:"var(--ink)", fontFamily:"Raleway, sans-serif", fontSize:11, fontWeight:600, letterSpacing:"0.18em", textTransform:"uppercase", cursor:"pointer", transition:"all 0.2s" }}
          >
            Enter
          </button>
        </div>
        {/* Divider */}
        <div style={{ display:"flex", alignItems:"center", gap:12, margin:"28px 0 0" }}>
          <div style={{ flex:1, height:1, background:"linear-gradient(to right, transparent, rgba(175,155,105,0.2))" }}/>
          <span style={{ color:"var(--gold)", fontSize:12 }}>✦</span>
          <div style={{ flex:1, height:1, background:"linear-gradient(to left, transparent, rgba(175,155,105,0.2))" }}/>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem(GATE_KEY) === "1");
  const [activeGroup, setActiveGroup] = useState("overview");
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [syncStatus, setSyncStatus] = useState("synced"); // "synced" | "saving" | "error"
  const skipSave = useRef(true); // prevent saving on initial load

  const [weddingInfo, setWeddingInfo] = useState(defaultInfo);
  const [guests, setGuests] = useState(defaultGuests);
  const [tables, setTables] = useState(defaultTables);
  const [vendors, setVendors] = useState(defaultVendors);
  const [checklist, setChecklist] = useState(defaultChecklist);
  const [registry, setRegistry] = useState(defaultRegistry);
  const [venues, setVenues] = useState(defaultVenues);
  const [budget, setBudget] = useState(defaultBudget);
  const [timeline, setTimeline] = useState(defaultTimeline);
  const [rsvps, setRsvps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [weddingParty, setWeddingParty] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [honeymoon, setHoneymoon] = useState({ items:[] });
  const [accommodations, setAccommodations] = useState({ guests:[], blockInfo:"" });
  const [decor, setDecor] = useState({ items:[] });
  const [invitations, setInvitations] = useState([]);
  const [ceremony, setCeremony] = useState({});
  const [dayOf, setDayOf] = useState({});

  // ── Load from Firestore on mount ──────────────────────────────────────────
  useEffect(() => {
    loadAll().then(data => {
      if (data) {
        try { if (data.weddingInfo) setWeddingInfo(JSON.parse(data.weddingInfo)); } catch {}
        try { if (data.guests)      setGuests(JSON.parse(data.guests)); }           catch {}
        try { if (data.tables)      setTables(JSON.parse(data.tables)); }           catch {}
        try { if (data.vendors)     setVendors(JSON.parse(data.vendors)); }         catch {}
        try { if (data.checklist)   setChecklist(JSON.parse(data.checklist)); }     catch {}
        try { if (data.registry)    setRegistry(JSON.parse(data.registry)); }       catch {}
        try { if (data.venues)      setVenues(JSON.parse(data.venues)); }           catch {}
        try { if (data.budget)      setBudget(JSON.parse(data.budget)); }           catch {}
        try { if (data.timeline)    setTimeline(JSON.parse(data.timeline)); }       catch {}
        try { if (data.rsvps)          setRsvps(JSON.parse(data.rsvps)); }          catch {}
        try { if (data.contacts)       setContacts(JSON.parse(data.contacts)); }       catch {}
        try { if (data.notes)          setNotes(JSON.parse(data.notes)); }             catch {}
        try { if (data.payments)       setPayments(JSON.parse(data.payments)); }       catch {}
        try { if (data.weddingParty)   setWeddingParty(JSON.parse(data.weddingParty)); } catch {}
        try { if (data.gifts)          setGifts(JSON.parse(data.gifts)); }             catch {}
        try { if (data.honeymoon)      setHoneymoon(JSON.parse(data.honeymoon)); }     catch {}
        try { if (data.accommodations) setAccommodations(JSON.parse(data.accommodations)); } catch {}
        try { if (data.decor)          setDecor(JSON.parse(data.decor)); }             catch {}
        try { if (data.invitations)    setInvitations(JSON.parse(data.invitations)); } catch {}
        try { if (data.ceremony)       setCeremony(JSON.parse(data.ceremony)); }       catch {}
        try { if (data.dayOf)          setDayOf(JSON.parse(data.dayOf)); }             catch {}
      }
      setLoading(false);
      // Allow saves after a short delay so initial state-sets don't trigger saves
      setTimeout(() => { skipSave.current = false; }, 300);
    });
  }, []);

  // ── Real-time listener: apply partner's changes ───────────────────────────
  useEffect(() => {
    const unsub = subscribeToChanges(data => {
      if (skipSave.current) return; // ignore during initial load
      try { if (data.weddingInfo) setWeddingInfo(JSON.parse(data.weddingInfo)); } catch {}
      try { if (data.guests)      setGuests(JSON.parse(data.guests)); }           catch {}
      try { if (data.tables)      setTables(JSON.parse(data.tables)); }           catch {}
      try { if (data.vendors)     setVendors(JSON.parse(data.vendors)); }         catch {}
      try { if (data.checklist)   setChecklist(JSON.parse(data.checklist)); }     catch {}
      try { if (data.registry)    setRegistry(JSON.parse(data.registry)); }       catch {}
      try { if (data.venues)      setVenues(JSON.parse(data.venues)); }           catch {}
      try { if (data.budget)      setBudget(JSON.parse(data.budget)); }           catch {}
      try { if (data.timeline)    setTimeline(JSON.parse(data.timeline)); }       catch {}
      try { if (data.rsvps)          setRsvps(JSON.parse(data.rsvps)); }          catch {}
      try { if (data.contacts)       setContacts(JSON.parse(data.contacts)); }       catch {}
      try { if (data.notes)          setNotes(JSON.parse(data.notes)); }             catch {}
      try { if (data.payments)       setPayments(JSON.parse(data.payments)); }       catch {}
      try { if (data.weddingParty)   setWeddingParty(JSON.parse(data.weddingParty)); } catch {}
      try { if (data.gifts)          setGifts(JSON.parse(data.gifts)); }             catch {}
      try { if (data.honeymoon)      setHoneymoon(JSON.parse(data.honeymoon)); }     catch {}
      try { if (data.accommodations) setAccommodations(JSON.parse(data.accommodations)); } catch {}
      try { if (data.decor)          setDecor(JSON.parse(data.decor)); }             catch {}
      try { if (data.invitations)    setInvitations(JSON.parse(data.invitations)); } catch {}
      try { if (data.ceremony)       setCeremony(JSON.parse(data.ceremony)); }       catch {}
      try { if (data.dayOf)          setDayOf(JSON.parse(data.dayOf)); }             catch {}
    });
    return () => unsub();
  }, []);

  // ── Auto-save helpers ──────────────────────────────────────────────────────
  const autosave = (key, value) => {
    if (skipSave.current) return;
    setSyncStatus("saving");
    saveKey(key, value).then(() => setSyncStatus("synced")).catch(() => setSyncStatus("error"));
  };

  useEffect(() => { autosave("weddingInfo", weddingInfo); }, [weddingInfo]);
  useEffect(() => { autosave("guests",      guests);      }, [guests]);
  useEffect(() => { autosave("tables",      tables);      }, [tables]);
  useEffect(() => { autosave("vendors",     vendors);     }, [vendors]);
  useEffect(() => { autosave("checklist",   checklist);   }, [checklist]);
  useEffect(() => { autosave("registry",    registry);    }, [registry]);
  useEffect(() => { autosave("venues",      venues);      }, [venues]);
  useEffect(() => { autosave("budget",      budget);      }, [budget]);
  useEffect(() => { autosave("timeline",    timeline);    }, [timeline]);
  useEffect(() => { autosave("rsvps",          rsvps);          }, [rsvps]);
  useEffect(() => { autosave("contacts",       contacts);       }, [contacts]);
  useEffect(() => { autosave("notes",          notes);          }, [notes]);
  useEffect(() => { autosave("payments",       payments);       }, [payments]);
  useEffect(() => { autosave("weddingParty",   weddingParty);   }, [weddingParty]);
  useEffect(() => { autosave("gifts",          gifts);          }, [gifts]);
  useEffect(() => { autosave("honeymoon",      honeymoon);      }, [honeymoon]);
  useEffect(() => { autosave("accommodations", accommodations); }, [accommodations]);
  useEffect(() => { autosave("decor",          decor);          }, [decor]);
  useEffect(() => { autosave("invitations",    invitations);    }, [invitations]);
  useEffect(() => { autosave("ceremony",       ceremony);       }, [ceremony]);
  useEffect(() => { autosave("dayOf",          dayOf);          }, [dayOf]);

  const days = weddingInfo.date ? daysUntil(weddingInfo.date) : null;

  if (!unlocked) return <DashboardGate onUnlock={() => setUnlocked(true)} />;

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#131813", flexDirection: "column", gap: 16 }}>
      <style>{CSS}</style>
      <Icon name="sparkle" size={48} color="var(--gold)"/>
      <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, color: "var(--rose-light)" }}>
        Loading your enchanted dashboard…
      </div>
    </div>
  );

  return (
    <>
      <style>{CSS}</style>
      <div className="app">

        {/* ── Header ── */}
        <header className="header">
          <FairyDust/>
          <div className="header-inner">
            <div className="header-title">
              <div className="header-crest">
                <Icon name="sparkle" size={24} color="var(--gold)"/>
              </div>
              <div>
                <div className="header-names">
                  {weddingInfo.partner1} &amp; {weddingInfo.partner2}
                </div>
                <div className="header-sub">
                  {weddingInfo.theme || "Wedding Planning"}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
              {/* Sync indicator */}
              <div style={{ fontSize: 10, letterSpacing: 1.5, color: syncStatus === "saving" ? "var(--gold)" : syncStatus === "error" ? "#c04040" : "var(--sage)", textTransform: "uppercase", fontWeight: 600 }}>
                {syncStatus === "saving" ? "✦ Saving…" : syncStatus === "error" ? "✗ Save error" : "✦ Synced"}
              </div>
              {days !== null && (
                <div className="countdown-box">
                  <div className="countdown-num">{Math.max(0, days)}</div>
                  <div className="countdown-label">Days to Go</div>
                </div>
              )}
              <button className="header-edit-btn" onClick={() => setShowInfo(true)}>
                <Icon name="wand" size={13} color="var(--gold)"/> Edit Details
              </button>
            </div>
          </div>
        </header>

        {/* ── Top Nav: group names, horizontal, gold glow when active ── */}
        <nav className="nav">
          {TAB_GROUPS.map(g => (
            <div
              key={g.id}
              className={`nav-item ${activeGroup === g.id ? "active" : ""}`}
              onClick={() => { setActiveGroup(g.id); if (g.tabs.length > 0) setTab(g.tabs[0].id); }}
            >
              <Icon name={g.icon} size={15} color={activeGroup === g.id ? "var(--gold)" : "var(--ink-faint)"}/>
              {g.label}
            </div>
          ))}
          <a
            href="https://bridalparty.thepritchardwedding.com"
            target="_blank" rel="noopener"
            className="nav-item bridal-item"
          >
            <Icon name="flower" size={15} color="var(--ink-faint)"/>
            Bridal Party
          </a>
        </nav>

        {/* ── Content ── */}
        <div className="dash-body">
          {TAB_GROUPS.find(g => g.id === activeGroup)?.tabs.length > 1 && (
            <aside className="dash-sidebar">
              {TAB_GROUPS.find(g => g.id === activeGroup)?.tabs.map(t => (
                <div key={t.id} className={`sidebar-item ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
                  <Icon name={t.icon} size={14} color={tab === t.id ? "var(--rose)" : "var(--ink-dim)"}/>
                  {t.label}
                </div>
              ))}
            </aside>
          )}
        <main className="main dash-content">
          {tab === "overview"  && <OverviewTab  guests={guests} vendors={vendors} checklist={checklist} budget={budget} weddingInfo={weddingInfo}/>}
          {tab === "guests"    && <GuestTab     guests={guests} setGuests={setGuests} tables={tables}/>}
          {tab === "seating"   && <SeatingTab   guests={guests} setGuests={setGuests} tables={tables} setTables={setTables}/>}
          {tab === "vendors"   && <VendorTab    vendors={vendors} setVendors={setVendors}/>}
          {tab === "checklist" && <ChecklistTab checklist={checklist} setChecklist={setChecklist}/>}
          {tab === "registry"  && <RegistryTab  registry={registry} setRegistry={setRegistry}/>}
          {tab === "venues"    && <VenueTab     venues={venues} setVenues={setVenues}/>}
          {tab === "budget"    && <BudgetTab    budget={budget} setBudget={setBudget}/>}
          {tab === "timeline"  && <TimelineTab  timeline={timeline} setTimeline={setTimeline}/>}
          {tab === "rsvps"          && <RsvpTab           rsvps={rsvps} setRsvps={setRsvps}/>}
          {tab === "meals"          && <MealSummaryTab    rsvps={rsvps}/>}
          {tab === "songs"          && <SongsTab          rsvps={rsvps}/>}
          {tab === "contacts"       && <ContactsTab       contacts={contacts} setContacts={setContacts}/>}
          {tab === "notes"          && <NotesTab          notes={notes} setNotes={setNotes}/>}
          {tab === "payments"       && <PaymentsTab       payments={payments} setPayments={setPayments}/>}
          {tab === "weddingparty"   && <WeddingPartyTab   weddingParty={weddingParty} setWeddingParty={setWeddingParty}/>}
          {tab === "ceremony"       && <CeremonyTab       ceremony={ceremony} setCeremony={setCeremony}/>}
          {tab === "gifts"          && <GiftTrackerTab    gifts={gifts} setGifts={setGifts}/>}
          {tab === "honeymoon"      && <HoneymoonTab      honeymoon={honeymoon} setHoneymoon={setHoneymoon}/>}
          {tab === "accommodations" && <AccommodationsTab accommodations={accommodations} setAccommodations={setAccommodations}/>}
          {tab === "decor"          && <DecorTab          decor={decor} setDecor={setDecor}/>}
          {tab === "invitations"    && <InvitationsTab    invitations={invitations} setInvitations={setInvitations}/>}
          {tab === "dayof"          && <DayOfTab          dayOf={dayOf} setDayOf={setDayOf}/>}
        </main>
        </div>

        {showInfo && <InfoModal info={weddingInfo} setInfo={setWeddingInfo} onClose={() => setShowInfo(false)}/>}
      </div>
    </>
  );
}

// ── Song Requests Tab ─────────────────────────────────────────────────────────
function SongsTab({ rsvps }) {
  const songs = rsvps.filter(r => r.song && r.song !== "—" && r.attending === "yes")
    .map(r => ({ song: r.song, from: `${r.firstname} ${r.lastname}` }));
  const [search, setSearch] = useState("");
  const filtered = songs.filter(s => s.song.toLowerCase().includes(search.toLowerCase()) || s.from.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="sec-header">
        <div className="card-title" style={{margin:0}}><Icon name="time" size={16} color="var(--gold)"/>Song Requests <span style={{fontSize:14,color:"var(--ink-dim)",fontFamily:"Raleway"}}>({songs.length})</span></div>
      </div>
      {songs.length === 0 ? (
        <div className="card" style={{textAlign:"center",padding:"48px 24px"}}>
          <Icon name="time" size={36} color="var(--ink-faint)"/>
          <p style={{color:"var(--ink-faint)",fontSize:13,marginTop:12,fontStyle:"italic"}}>Song requests from guests will appear here automatically when they RSVP.</p>
        </div>
      ) : (
        <>
          <input style={{width:"100%",padding:"9px 14px",borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,outline:"none",marginBottom:16}} placeholder="Search songs or guests…" value={search} onChange={e=>setSearch(e.target.value)}/>
          <div className="card" style={{padding:0}}>
            <table><thead><tr><th>#</th><th>Song / Artist</th><th>Requested By</th></tr></thead>
              <tbody>
                {filtered.map((s,i) => (
                  <tr key={i}>
                    <td style={{color:"var(--ink-faint)",fontSize:12,width:40}}>{i+1}</td>
                    <td><div style={{fontWeight:600,fontSize:13,color:"var(--gold)"}}>♪ {s.song}</div></td>
                    <td style={{fontSize:12,color:"var(--ink-dim)"}}>{s.from}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{marginTop:14,fontSize:11,color:"var(--ink-faint)",textAlign:"center"}}>
            {filtered.length} of {songs.length} songs shown · Share this list with your DJ
          </div>
        </>
      )}
    </div>
  );
}

// ── Meal Summary Tab ──────────────────────────────────────────────────────────
function MealSummaryTab({ rsvps }) {
  const attending = rsvps.filter(r => r.attending === "yes");
  const mealCounts = {};
  const dietary = [];
  attending.forEach(r => {
    if (r.meal) {
      r.meal.split(" | ").forEach(m => {
        const val = m.replace(/Guest \d+: /,"").trim();
        if (val && val !== "Not selected") mealCounts[val] = (mealCounts[val]||0) + 1;
      });
    }
    if (r.dietary && r.dietary !== "None" && r.dietary !== "—") {
      dietary.push({ name:`${r.firstname} ${r.lastname}`, note:r.dietary });
    }
  });
  const totalGuests = attending.reduce((s,r) => s + Number(r.guests||1), 0);

  return (
    <div>
      <div className="card-title" style={{margin:"0 0 20px"}}><Icon name="scroll" size={16} color="var(--gold)"/>Meal & Dietary Summary</div>
      <div className="stats-grid" style={{gridTemplateColumns:"repeat(3,1fr)",marginBottom:22}}>
        <div className="stat-card"><div className="stat-num">{totalGuests}</div><div className="stat-label">Total Dining</div></div>
        <div className="stat-card"><div className="stat-num">{Object.keys(mealCounts).length}</div><div className="stat-label">Meal Options</div></div>
        <div className="stat-card"><div className="stat-num" style={{color:"#c8a96e"}}>{dietary.length}</div><div className="stat-label">Dietary Needs</div></div>
      </div>
      <div className="grid-2">
        <div className="card">
          <div className="card-title" style={{fontSize:16}}><Icon name="gift" size={14} color="var(--gold)"/>Meal Counts</div>
          {Object.keys(mealCounts).length === 0 ? <p style={{color:"var(--ink-faint)",fontSize:13,fontStyle:"italic"}}>No meal selections yet</p> :
            Object.entries(mealCounts).sort((a,b)=>b[1]-a[1]).map(([meal,count]) => (
              <div key={meal} style={{marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4,fontSize:13}}>
                  <span style={{textTransform:"capitalize"}}>{meal}</span>
                  <strong style={{color:"var(--gold)"}}>{count}</strong>
                </div>
                <div style={{height:6,background:"rgba(255,255,255,0.05)",borderRadius:3,overflow:"hidden"}}>
                  <div style={{height:"100%",borderRadius:3,background:"linear-gradient(90deg,var(--rose-dim),var(--rose))",width:`${(count/totalGuests)*100}%`,transition:"width 0.4s"}}/>
                </div>
              </div>
            ))
          }
        </div>
        <div className="card">
          <div className="card-title" style={{fontSize:16}}><Icon name="leaf" size={14} color="var(--gold)"/>Dietary Restrictions</div>
          {dietary.length === 0 ? <p style={{color:"var(--ink-faint)",fontSize:13,fontStyle:"italic"}}>No dietary restrictions noted</p> :
            dietary.map((d,i) => (
              <div key={i} style={{padding:"10px 0",borderBottom:"1px solid rgba(175,155,105,0.07)"}}>
                <div style={{fontWeight:600,fontSize:13,color:"var(--rose-light)"}}>{d.name}</div>
                <div style={{fontSize:12,color:"var(--ink-dim)",marginTop:2}}>{d.note}</div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}

// ── Contacts Tab ──────────────────────────────────────────────────────────────
function ContactsTab({ contacts, setContacts }) {
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("All");
  const CATS = ["Vendor","Wedding Party","Family","Venue","Officiant","Coordinator","Other"];
  const blank = { name:"", role:"", category:"Vendor", phone:"", email:"", notes:"" };
  const [form, setForm] = useState(blank);
  const cats = ["All", ...new Set(contacts.map(c=>c.category))];
  const filtered = filter === "All" ? contacts : contacts.filter(c=>c.category===filter);
  function openAdd() { setForm(blank); setEditing(null); setModal(true); }
  function openEdit(c) { setForm({...c}); setEditing(c.id); setModal(true); }
  function save() { if(!form.name) return; const c={...form,id:editing||uid()}; if(editing) setContacts(p=>p.map(x=>x.id===editing?c:x)); else setContacts(p=>[...p,c]); setModal(false); }
  function remove(id) { setContacts(p=>p.filter(c=>c.id!==id)); }

  return (
    <div>
      <div className="sec-header">
        <div className="card-title" style={{margin:0}}><Icon name="guests" size={16} color="var(--gold)"/>Contacts Directory</div>
        <button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={13}/>Add Contact</button>
      </div>
      <div className="filter-bar">{cats.map(c=><button key={c} className={`filter-chip ${filter===c?"active":""}`} onClick={()=>setFilter(c)}>{c}</button>)}</div>
      <div className="card" style={{padding:0}}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Role</th><th>Category</th><th>Phone</th><th>Email</th><th>Notes</th><th></th></tr></thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td style={{fontWeight:600,fontSize:13,color:"var(--rose-light)"}}>{c.name}</td>
                  <td style={{fontSize:12}}>{c.role}</td>
                  <td><span className="badge" style={{background:"rgba(200,145,122,0.1)",color:"var(--rose)",fontSize:9}}>{c.category}</span></td>
                  <td style={{fontSize:12}}>{c.phone ? <a href={`tel:${c.phone}`} style={{color:"var(--sage)",textDecoration:"none"}}>{c.phone}</a> : "—"}</td>
                  <td style={{fontSize:12}}>{c.email ? <a href={`mailto:${c.email}`} style={{color:"var(--sage)",textDecoration:"none"}}>{c.email}</a> : "—"}</td>
                  <td style={{fontSize:11,color:"var(--ink-dim)",maxWidth:160}}>{c.notes||"—"}</td>
                  <td><div style={{display:"flex",gap:4}}><button className="btn-icon" onClick={()=>openEdit(c)}><Icon name="edit" size={13}/></button><button className="btn-icon" onClick={()=>remove(c.id)}><Icon name="trash" size={13}/></button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length===0 && <div className="empty-state" style={{padding:40}}><Icon name="guests" size={36} color="var(--ink-faint)"/><p style={{marginTop:10,color:"var(--ink-faint)"}}>No contacts yet</p></div>}
        </div>
      </div>
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-title"><Icon name="flower" size={16} color="var(--rose)"/>{editing?"Edit Contact":"Add Contact"}</div>
            <div className="form-row"><div className="form-group" style={{flex:2}}><label>Name *</label><input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Full name or business"/></div><div className="form-group"><label>Category</label><select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>{CATS.map(c=><option key={c}>{c}</option>)}</select></div></div>
            <div className="form-group" style={{marginBottom:14}}><label>Role / Title</label><input value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))} placeholder="e.g. Lead Photographer, Maid of Honor"/></div>
            <div className="form-row"><div className="form-group"><label>Phone</label><input value={form.phone} onChange={e=>setForm(p=>({...p,phone:e.target.value}))}/></div><div className="form-group"><label>Email</label><input value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/></div></div>
            <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>{editing?"Save":"Add"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Notes Tab ─────────────────────────────────────────────────────────────────
function NotesTab({ notes, setNotes }) {
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("All");
  const CATS = ["General","Venue","Catering","Attire","Decor","Honeymoon","Vows","Music","Ideas","Other"];
  const COLORS = { General:"var(--ink-dim)", Venue:"var(--sage)", Catering:"var(--gold)", Attire:"var(--rose)", Decor:"#9a7aaa", Honeymoon:"#6a9ab0", Vows:"var(--rose-light)", Music:"var(--gold)", Ideas:"var(--sage)", Other:"var(--ink-dim)" };
  const blank = { title:"", content:"", category:"General", pinned:false };
  const [form, setForm] = useState(blank);
  const cats = ["All",...new Set(notes.map(n=>n.category))];
  const filtered = (filter==="All" ? notes : notes.filter(n=>n.category===filter)).sort((a,b)=>(b.pinned?1:0)-(a.pinned?1:0));
  function openAdd() { setForm(blank); setEditing(null); setModal(true); }
  function openEdit(n) { setForm({...n}); setEditing(n.id); setModal(true); }
  function save() { if(!form.title) return; const n={...form,id:editing||uid(),updatedAt:new Date().toISOString()}; if(editing) setNotes(p=>p.map(x=>x.id===editing?n:x)); else setNotes(p=>[n,...p]); setModal(false); }
  function remove(id) { setNotes(p=>p.filter(n=>n.id!==id)); }
  function togglePin(id) { setNotes(p=>p.map(n=>n.id===id?{...n,pinned:!n.pinned}:n)); }

  return (
    <div>
      <div className="sec-header">
        <div className="card-title" style={{margin:0}}><Icon name="scroll" size={16} color="var(--gold)"/>Notes &amp; Decisions</div>
        <button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={13}/>Add Note</button>
      </div>
      <div className="filter-bar">{cats.map(c=><button key={c} className={`filter-chip ${filter===c?"active":""}`} onClick={()=>setFilter(c)}>{c}</button>)}</div>
      {notes.length===0 && <div className="card" style={{textAlign:"center",padding:"48px 24px"}}><Icon name="scroll" size={36} color="var(--ink-faint)"/><p style={{color:"var(--ink-faint)",fontSize:13,marginTop:12,fontStyle:"italic"}}>A scratchpad for ideas, decisions, and everything in between.</p><button className="btn btn-primary" style={{marginTop:16}} onClick={openAdd}><Icon name="plus" size={13}/>Add First Note</button></div>}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
        {filtered.map(n => (
          <div key={n.id} style={{background:"var(--forest-card)",borderRadius:"var(--radius)",border:`1px solid ${n.pinned?"rgba(200,169,110,0.4)":"var(--border)"}`,padding:18,position:"relative",transition:"all 0.15s"}}>
            {n.pinned && <div style={{position:"absolute",top:12,right:12,color:"var(--gold)",fontSize:12}}>✦</div>}
            <div style={{display:"flex",gap:8,marginBottom:8,alignItems:"center"}}>
              <span className="badge" style={{background:`${COLORS[n.category]||"var(--ink-dim)"}18`,color:COLORS[n.category]||"var(--ink-dim)",fontSize:9}}>{n.category}</span>
            </div>
            <div style={{fontWeight:600,fontSize:14,color:"var(--rose-light)",marginBottom:8}}>{n.title}</div>
            <div style={{fontSize:13,color:"var(--ink)",lineHeight:1.7,whiteSpace:"pre-wrap"}}>{n.content}</div>
            {n.updatedAt && <div style={{fontSize:10,color:"var(--ink-faint)",marginTop:10}}>{new Date(n.updatedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</div>}
            <div style={{display:"flex",gap:6,marginTop:12}}>
              <button className="btn-icon" onClick={()=>togglePin(n.id)} title={n.pinned?"Unpin":"Pin"}><Icon name="sparkle" size={12} color={n.pinned?"var(--gold)":"currentColor"}/></button>
              <button className="btn-icon" onClick={()=>openEdit(n)}><Icon name="edit" size={12}/></button>
              <button className="btn-icon" onClick={()=>remove(n.id)}><Icon name="trash" size={12}/></button>
            </div>
          </div>
        ))}
      </div>
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-title"><Icon name="scroll" size={16} color="var(--rose)"/>{editing?"Edit Note":"New Note"}</div>
            <div className="form-row"><div className="form-group" style={{flex:2}}><label>Title *</label><input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="What's this about?"/></div><div className="form-group"><label>Category</label><select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>{CATS.map(c=><option key={c}>{c}</option>)}</select></div></div>
            <div className="form-group" style={{marginBottom:14}}><label>Content</label><textarea value={form.content} onChange={e=>setForm(p=>({...p,content:e.target.value}))} style={{minHeight:140}} placeholder="Ideas, decisions, reminders…"/></div>
            <label style={{display:"flex",gap:8,fontSize:12,cursor:"pointer",color:"var(--ink-dim)",alignItems:"center",marginBottom:14}}><input type="checkbox" checked={form.pinned} onChange={e=>setForm(p=>({...p,pinned:e.target.checked}))}/>Pin to top</label>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>{editing?"Save":"Add Note"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Payments Tab ──────────────────────────────────────────────────────────────
function PaymentsTab({ payments, setPayments }) {
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { vendor:"", description:"", amount:"", dueDate:"", paid:false, paidDate:"", confirmationNum:"", method:"", notes:"" };
  const [form, setForm] = useState(blank);
  const sorted = [...payments].sort((a,b)=>{ if(a.paid!==b.paid) return a.paid?1:-1; return new Date(a.dueDate||"9999")-new Date(b.dueDate||"9999"); });
  const totalDue = payments.filter(p=>!p.paid).reduce((s,p)=>s+Number(p.amount||0),0);
  const totalPaid = payments.filter(p=>p.paid).reduce((s,p)=>s+Number(p.amount||0),0);
  function openAdd() { setForm(blank); setEditing(null); setModal(true); }
  function openEdit(p) { setForm({...p}); setEditing(p.id); setModal(true); }
  function save() { if(!form.vendor) return; const p={...form,amount:Number(form.amount),id:editing||uid()}; if(editing) setPayments(prev=>prev.map(x=>x.id===editing?p:x)); else setPayments(prev=>[...prev,p]); setModal(false); }
  function remove(id) { setPayments(p=>p.filter(x=>x.id!==id)); }
  function togglePaid(id) { setPayments(p=>p.map(x=>x.id===id?{...x,paid:!x.paid,paidDate:!x.paid?new Date().toISOString().split("T")[0]:x.paidDate}:x)); }

  return (
    <div>
      <div className="sec-header">
        <div className="card-title" style={{margin:0}}><Icon name="budget" size={16} color="var(--gold)"/>Payments Tracker</div>
        <button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={13}/>Add Payment</button>
      </div>
      <div className="stats-grid" style={{gridTemplateColumns:"repeat(3,1fr)",marginBottom:20}}>
        <div className="stat-card"><div className="stat-num" style={{fontSize:26,color:"#c04040"}}>{currency(totalDue)}</div><div className="stat-label">Outstanding</div></div>
        <div className="stat-card"><div className="stat-num" style={{fontSize:26,color:"var(--sage)"}}>{currency(totalPaid)}</div><div className="stat-label">Paid</div></div>
        <div className="stat-card"><div className="stat-num" style={{fontSize:26}}>{currency(totalDue+totalPaid)}</div><div className="stat-label">Total</div></div>
      </div>
      <div className="card" style={{padding:0}}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Vendor</th><th>Description</th><th>Amount</th><th>Due Date</th><th>Status</th><th>Confirmation</th><th></th></tr></thead>
            <tbody>
              {sorted.map(p => {
                const due = p.dueDate ? daysUntil(p.dueDate) : null;
                return (
                  <tr key={p.id} style={{opacity:p.paid?0.55:1}}>
                    <td style={{fontWeight:600,fontSize:13,color:"var(--rose-light)"}}>{p.vendor}</td>
                    <td style={{fontSize:12}}>{p.description}</td>
                    <td style={{fontWeight:600,color:"var(--gold)"}}>{currency(p.amount)}</td>
                    <td style={{fontSize:12,color:!p.paid&&due!==null&&due<14?"#c04040":"var(--ink-dim)"}}>{p.dueDate||"—"}{!p.paid&&due!==null&&due<7&&<span style={{marginLeft:6,fontSize:9,color:"#c04040"}}>⚠ {due<0?"Overdue":`${due}d`}</span>}</td>
                    <td>
                      <span className="badge" style={{background:p.paid?"rgba(122,170,128,0.15)":"rgba(192,64,64,0.12)",color:p.paid?"var(--sage)":"#c06060",cursor:"pointer",fontSize:9}} onClick={()=>togglePaid(p.id)}>
                        {p.paid?"✦ Paid":"Pending"}
                      </span>
                    </td>
                    <td style={{fontSize:11,color:"var(--ink-dim)"}}>{p.confirmationNum||"—"}</td>
                    <td><div style={{display:"flex",gap:4}}><button className="btn-icon" onClick={()=>openEdit(p)}><Icon name="edit" size={13}/></button><button className="btn-icon" onClick={()=>remove(p.id)}><Icon name="trash" size={13}/></button></div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {payments.length===0&&<div className="empty-state" style={{padding:40}}><Icon name="budget" size={36} color="var(--ink-faint)"/><p style={{marginTop:10,color:"var(--ink-faint)"}}>Track deposits, final payments, and confirmation numbers</p></div>}
        </div>
      </div>
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-title"><Icon name="budget" size={16} color="var(--rose)"/>{editing?"Edit Payment":"Add Payment"}</div>
            <div className="form-row"><div className="form-group" style={{flex:2}}><label>Vendor / Payee *</label><input value={form.vendor} onChange={e=>setForm(p=>({...p,vendor:e.target.value}))}/></div><div className="form-group"><label>Amount ($)</label><input type="number" value={form.amount} onChange={e=>setForm(p=>({...p,amount:e.target.value}))}/></div></div>
            <div className="form-group" style={{marginBottom:14}}><label>Description</label><input value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="e.g. Deposit, Final payment"/></div>
            <div className="form-row"><div className="form-group"><label>Due Date</label><input type="date" value={form.dueDate} onChange={e=>setForm(p=>({...p,dueDate:e.target.value}))}/></div><div className="form-group"><label>Payment Method</label><input value={form.method} onChange={e=>setForm(p=>({...p,method:e.target.value}))} placeholder="e.g. Check, Zelle"/></div></div>
            <div className="form-group" style={{marginBottom:14}}><label>Confirmation #</label><input value={form.confirmationNum} onChange={e=>setForm(p=>({...p,confirmationNum:e.target.value}))}/></div>
            <label style={{display:"flex",gap:8,fontSize:12,cursor:"pointer",color:"var(--ink-dim)",alignItems:"center",marginBottom:14}}><input type="checkbox" checked={form.paid} onChange={e=>setForm(p=>({...p,paid:e.target.checked}))}/>Mark as paid</label>
            {form.paid && <div className="form-group" style={{marginBottom:14}}><label>Date Paid</label><input type="date" value={form.paidDate} onChange={e=>setForm(p=>({...p,paidDate:e.target.value}))}/></div>}
            <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>{editing?"Save":"Add"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Wedding Party Tab ─────────────────────────────────────────────────────────
function WeddingPartyTab({ weddingParty, setWeddingParty }) {
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const ROLES = ["Maid of Honor","Best Man","Bridesmaid","Groomsman","Flower Girl","Ring Bearer","Jr. Bridesmaid","Jr. Groomsman","Reader","Officiant","Other"];
  const blank = { name:"", role:"Bridesmaid", side:"bride", phone:"", email:"", attire:"", attireColor:"", notes:"", confirmed:false };
  const [form, setForm] = useState(blank);
  const bride = weddingParty.filter(p=>p.side==="bride");
  const groom = weddingParty.filter(p=>p.side==="groom");
  const other = weddingParty.filter(p=>p.side==="other");
  function openAdd() { setForm(blank); setEditing(null); setModal(true); }
  function openEdit(p) { setForm({...p}); setEditing(p.id); setModal(true); }
  function save() { if(!form.name) return; const p={...form,id:editing||uid()}; if(editing) setWeddingParty(prev=>prev.map(x=>x.id===editing?p:x)); else setWeddingParty(prev=>[...prev,p]); setModal(false); }
  function remove(id) { setWeddingParty(p=>p.filter(x=>x.id!==id)); }
  function toggleConfirmed(id) { setWeddingParty(p=>p.map(x=>x.id===id?{...x,confirmed:!x.confirmed}:x)); }

  const PartyCard = ({p}) => (
    <div style={{background:"var(--forest-lift)",borderRadius:"var(--radius-sm)",border:`1px solid ${p.confirmed?"rgba(122,170,128,0.3)":"var(--border)"}`,padding:14,marginBottom:8,display:"flex",gap:12,alignItems:"flex-start"}}>
      <div style={{width:38,height:38,borderRadius:"50%",background:"var(--forest-card)",border:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <Icon name="dove" size={16} color="var(--gold)"/>
      </div>
      <div style={{flex:1}}>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <span style={{fontWeight:600,fontSize:13,color:"var(--rose-light)"}}>{p.name}</span>
          <span className="badge" style={{background:"rgba(200,145,122,0.1)",color:"var(--rose)",fontSize:9}}>{p.role}</span>
          <span className="badge" style={{background:p.confirmed?"rgba(122,170,128,0.15)":"rgba(175,155,105,0.1)",color:p.confirmed?"var(--sage)":"var(--ink-dim)",fontSize:9,cursor:"pointer"}} onClick={()=>toggleConfirmed(p.id)}>{p.confirmed?"✦ Confirmed":"Pending"}</span>
        </div>
        {p.attire && <div style={{fontSize:11,color:"var(--ink-dim)",marginTop:3}}>Attire: {p.attire}{p.attireColor&&` · ${p.attireColor}`}</div>}
        {p.phone && <div style={{fontSize:11,color:"var(--ink-dim)"}}>{p.phone}{p.email&&` · ${p.email}`}</div>}
        {p.notes && <div style={{fontSize:11,color:"var(--sage)",fontStyle:"italic",marginTop:2}}>{p.notes}</div>}
      </div>
      <div style={{display:"flex",gap:4,flexShrink:0}}>
        <button className="btn-icon" onClick={()=>openEdit(p)}><Icon name="edit" size={12}/></button>
        <button className="btn-icon" onClick={()=>remove(p.id)}><Icon name="trash" size={12}/></button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="sec-header">
        <div className="card-title" style={{margin:0}}><Icon name="dove" size={16} color="var(--gold)"/>Wedding Party</div>
        <button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={13}/>Add Member</button>
      </div>
      <div className="stats-grid" style={{gridTemplateColumns:"repeat(3,1fr)",marginBottom:20}}>
        <div className="stat-card"><div className="stat-num">{weddingParty.length}</div><div className="stat-label">Total Members</div></div>
        <div className="stat-card"><div className="stat-num" style={{color:"var(--sage)"}}>{weddingParty.filter(p=>p.confirmed).length}</div><div className="stat-label">Confirmed</div></div>
        <div className="stat-card"><div className="stat-num" style={{color:"var(--gold)"}}>{weddingParty.filter(p=>!p.confirmed).length}</div><div className="stat-label">Pending</div></div>
      </div>
      <div className="grid-2">
        <div className="card">
          <div className="card-title" style={{fontSize:16}}><Icon name="flower" size={14} color="var(--rose)"/>Bride's Side ({bride.length})</div>
          {bride.map(p=><PartyCard key={p.id} p={p}/>)}
          {bride.length===0&&<p style={{color:"var(--ink-faint)",fontSize:13,fontStyle:"italic"}}>No members added yet</p>}
        </div>
        <div className="card">
          <div className="card-title" style={{fontSize:16}}><Icon name="ring" size={14} color="var(--gold)"/>Groom's Side ({groom.length})</div>
          {groom.map(p=><PartyCard key={p.id} p={p}/>)}
          {groom.length===0&&<p style={{color:"var(--ink-faint)",fontSize:13,fontStyle:"italic"}}>No members added yet</p>}
        </div>
      </div>
      {other.length>0 && <div className="card" style={{marginTop:14}}><div className="card-title" style={{fontSize:16}}>Other Roles</div>{other.map(p=><PartyCard key={p.id} p={p}/>)}</div>}
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-title"><Icon name="dove" size={16} color="var(--rose)"/>{editing?"Edit Member":"Add Member"}</div>
            <div className="form-row"><div className="form-group" style={{flex:2}}><label>Name *</label><input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/></div><div className="form-group"><label>Side</label><select value={form.side} onChange={e=>setForm(p=>({...p,side:e.target.value}))}><option value="bride">Bride's</option><option value="groom">Groom's</option><option value="other">Other</option></select></div></div>
            <div className="form-group" style={{marginBottom:14}}><label>Role</label><select value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))}>{ROLES.map(r=><option key={r}>{r}</option>)}</select></div>
            <div className="form-row"><div className="form-group"><label>Phone</label><input value={form.phone} onChange={e=>setForm(p=>({...p,phone:e.target.value}))}/></div><div className="form-group"><label>Email</label><input value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/></div></div>
            <div className="form-row"><div className="form-group"><label>Attire</label><input value={form.attire} onChange={e=>setForm(p=>({...p,attire:e.target.value}))} placeholder="e.g. Suit, Floor-length gown"/></div><div className="form-group"><label>Color</label><input value={form.attireColor} onChange={e=>setForm(p=>({...p,attireColor:e.target.value}))} placeholder="e.g. Dusty rose"/></div></div>
            <label style={{display:"flex",gap:8,fontSize:12,cursor:"pointer",color:"var(--ink-dim)",alignItems:"center",marginBottom:14}}><input type="checkbox" checked={form.confirmed} onChange={e=>setForm(p=>({...p,confirmed:e.target.checked}))}/>Confirmed</label>
            <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>{editing?"Save":"Add"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ceremony Tab ──────────────────────────────────────────────────────────────
function CeremonyTab({ ceremony, setCeremony }) {
  const [activeSection, setActiveSection] = useState("vows");
  function update(key, val) { setCeremony(prev=>({...prev,[key]:val})); }

  return (
    <div>
      <div className="card-title" style={{margin:"0 0 20px"}}><Icon name="ring" size={16} color="var(--gold)"/>Ceremony Planner</div>
      <div className="filter-bar">
        {[["vows","Vows"],["order","Order of Events"],["readings","Readings"],["processional","Processional"],["notes","Officiant Notes"]].map(([id,label])=>(
          <button key={id} className={`filter-chip ${activeSection===id?"active":""}`} onClick={()=>setActiveSection(id)}>{label}</button>
        ))}
      </div>

      {activeSection==="vows" && (
        <div className="grid-2">
          <div className="card">
            <div className="card-title" style={{fontSize:16}}><Icon name="feather" size={14} color="var(--rose)"/>Khalib's Vows</div>
            <p style={{fontSize:11,color:"var(--ink-faint)",marginBottom:10,fontStyle:"italic"}}>Private — only visible in this dashboard</p>
            <textarea value={ceremony.vowsPartner1||""} onChange={e=>update("vowsPartner1",e.target.value)} placeholder="Write your vows here…" style={{width:"100%",minHeight:220,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Cormorant Garamond, serif",fontSize:15,lineHeight:1.8,outline:"none",resize:"vertical"}}/>
          </div>
          <div className="card">
            <div className="card-title" style={{fontSize:16}}><Icon name="feather" size={14} color="var(--rose)"/>Zoë's Vows</div>
            <p style={{fontSize:11,color:"var(--ink-faint)",marginBottom:10,fontStyle:"italic"}}>Private — only visible in this dashboard</p>
            <textarea value={ceremony.vowsPartner2||""} onChange={e=>update("vowsPartner2",e.target.value)} placeholder="Write your vows here…" style={{width:"100%",minHeight:220,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Cormorant Garamond, serif",fontSize:15,lineHeight:1.8,outline:"none",resize:"vertical"}}/>
          </div>
        </div>
      )}
      {activeSection==="order" && (
        <div className="card">
          <div className="card-title" style={{fontSize:16}}>Order of Events</div>
          <textarea value={ceremony.orderOfEvents||""} onChange={e=>update("orderOfEvents",e.target.value)} placeholder={"e.g.\n1. Guests are seated\n2. Processional begins\n3. Bride's entrance\n4. Welcome by officiant\n5. Readings\n6. Vows\n7. Ring exchange\n8. Pronouncement\n9. First kiss\n10. Recessional"} style={{width:"100%",minHeight:300,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.8,outline:"none",resize:"vertical"}}/>
        </div>
      )}
      {activeSection==="readings" && (
        <div className="card">
          <div className="card-title" style={{fontSize:16}}>Readings &amp; Readers</div>
          <textarea value={ceremony.readings||""} onChange={e=>update("readings",e.target.value)} placeholder={"Who is doing readings and what are they reading?\ne.g.\nReading 1: Aunt Carol — 1 Corinthians 13:4-8\nReading 2: Uncle James — Excerpt from The Alchemist"} style={{width:"100%",minHeight:200,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.8,outline:"none",resize:"vertical"}}/>
        </div>
      )}
      {activeSection==="processional" && (
        <div className="card">
          <div className="card-title" style={{fontSize:16}}>Processional &amp; Recessional</div>
          <textarea value={ceremony.processional||""} onChange={e=>update("processional",e.target.value)} placeholder={"Processional order and songs:\ne.g.\n- Grandparents seated — Canon in D\n- Parents seated\n- Wedding party: [names in order]\n- Ring bearer & flower girl\n- Bride entrance — [song]\n\nRecessional: [song]"} style={{width:"100%",minHeight:260,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.8,outline:"none",resize:"vertical"}}/>
        </div>
      )}
      {activeSection==="notes" && (
        <div className="card">
          <div className="card-title" style={{fontSize:16}}>Officiant Notes</div>
          <textarea value={ceremony.officiantNotes||""} onChange={e=>update("officiantNotes",e.target.value)} placeholder={"Notes for your officiant — tone, specific requests, things to include or avoid, pronunciation notes, etc."} style={{width:"100%",minHeight:200,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.8,outline:"none",resize:"vertical"}}/>
        </div>
      )}
    </div>
  );
}

// ── Gift Tracker Tab ──────────────────────────────────────────────────────────
function GiftTrackerTab({ gifts, setGifts }) {
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const blank = { giver:"", gift:"", value:"", receivedDate:"", thankYouSent:false, thankYouDate:"", notes:"" };
  const [form, setForm] = useState(blank);
  const filtered = filter==="all" ? gifts : filter==="pending" ? gifts.filter(g=>!g.thankYouSent) : gifts.filter(g=>g.thankYouSent);
  const totalValue = gifts.reduce((s,g)=>s+Number(g.value||0),0);
  const pendingTY = gifts.filter(g=>!g.thankYouSent).length;
  function openAdd() { setForm(blank); setEditing(null); setModal(true); }
  function openEdit(g) { setForm({...g}); setEditing(g.id); setModal(true); }
  function save() { if(!form.giver) return; const g={...form,value:Number(form.value),id:editing||uid()}; if(editing) setGifts(p=>p.map(x=>x.id===editing?g:x)); else setGifts(p=>[...p,g]); setModal(false); }
  function remove(id) { setGifts(p=>p.filter(g=>g.id!==id)); }
  function toggleTY(id) { setGifts(p=>p.map(g=>g.id===id?{...g,thankYouSent:!g.thankYouSent,thankYouDate:!g.thankYouSent?new Date().toISOString().split("T")[0]:g.thankYouDate}:g)); }

  return (
    <div>
      <div className="sec-header">
        <div className="card-title" style={{margin:0}}><Icon name="gift" size={16} color="var(--gold)"/>Gift Tracker</div>
        <button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={13}/>Add Gift</button>
      </div>
      <div className="stats-grid" style={{gridTemplateColumns:"repeat(3,1fr)",marginBottom:20}}>
        <div className="stat-card"><div className="stat-num">{gifts.length}</div><div className="stat-label">Gifts Received</div></div>
        <div className="stat-card"><div className="stat-num" style={{color:"var(--gold)"}}>{currency(totalValue)}</div><div className="stat-label">Total Value</div></div>
        <div className="stat-card"><div className="stat-num" style={{color:pendingTY>0?"#c04040":"var(--sage)"}}>{pendingTY}</div><div className="stat-label">Thank Yous Pending</div></div>
      </div>
      <div className="filter-bar">
        {[["all","All"],["pending","Thank You Pending"],["done","Thank You Sent"]].map(([val,label])=>(
          <button key={val} className={`filter-chip ${filter===val?"active":""}`} onClick={()=>setFilter(val)}>{label} {val==="pending"?`(${pendingTY})`:""}</button>
        ))}
      </div>
      <div className="card" style={{padding:0}}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>From</th><th>Gift</th><th>Value</th><th>Received</th><th>Thank You</th><th></th></tr></thead>
            <tbody>
              {filtered.map(g => (
                <tr key={g.id}>
                  <td style={{fontWeight:600,fontSize:13,color:"var(--rose-light)"}}>{g.giver}</td>
                  <td style={{fontSize:12}}>{g.gift||"—"}</td>
                  <td style={{color:"var(--gold)",fontWeight:600}}>{g.value?currency(g.value):"—"}</td>
                  <td style={{fontSize:12,color:"var(--ink-dim)"}}>{g.receivedDate||"—"}</td>
                  <td>
                    <span className="badge" style={{background:g.thankYouSent?"rgba(122,170,128,0.15)":"rgba(192,64,64,0.12)",color:g.thankYouSent?"var(--sage)":"#c06060",cursor:"pointer",fontSize:9}} onClick={()=>toggleTY(g.id)}>
                      {g.thankYouSent?`✦ Sent ${g.thankYouDate||""}`:"Pending"}
                    </span>
                  </td>
                  <td><div style={{display:"flex",gap:4}}><button className="btn-icon" onClick={()=>openEdit(g)}><Icon name="edit" size={13}/></button><button className="btn-icon" onClick={()=>remove(g.id)}><Icon name="trash" size={13}/></button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length===0&&<div className="empty-state" style={{padding:40}}><Icon name="gift" size={36} color="var(--ink-faint)"/><p style={{marginTop:10,color:"var(--ink-faint)"}}>Track gifts received and thank you notes</p></div>}
        </div>
      </div>
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-title"><Icon name="gift" size={16} color="var(--rose)"/>{editing?"Edit Gift":"Add Gift"}</div>
            <div className="form-row"><div className="form-group" style={{flex:2}}><label>From *</label><input value={form.giver} onChange={e=>setForm(p=>({...p,giver:e.target.value}))} placeholder="Who gave this gift?"/></div><div className="form-group"><label>Value ($)</label><input type="number" value={form.value} onChange={e=>setForm(p=>({...p,value:e.target.value}))}/></div></div>
            <div className="form-group" style={{marginBottom:14}}><label>Gift Description</label><input value={form.gift} onChange={e=>setForm(p=>({...p,gift:e.target.value}))} placeholder="e.g. KitchenAid Mixer, Cash, Gift card"/></div>
            <div className="form-row"><div className="form-group"><label>Date Received</label><input type="date" value={form.receivedDate} onChange={e=>setForm(p=>({...p,receivedDate:e.target.value}))}/></div><div className="form-group"><label>Thank You Date</label><input type="date" value={form.thankYouDate} onChange={e=>setForm(p=>({...p,thankYouDate:e.target.value}))}/></div></div>
            <label style={{display:"flex",gap:8,fontSize:12,cursor:"pointer",color:"var(--ink-dim)",alignItems:"center",marginBottom:14}}><input type="checkbox" checked={form.thankYouSent} onChange={e=>setForm(p=>({...p,thankYouSent:e.target.checked}))}/>Thank you note sent</label>
            <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>{editing?"Save":"Add Gift"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Honeymoon Tab ─────────────────────────────────────────────────────────────
function HoneymoonTab({ honeymoon, setHoneymoon }) {
  const [activeSection, setActiveSection] = useState("overview");
  const [modal, setModal] = useState(false);
  const [itemType, setItemType] = useState("flight");
  const [editing, setEditing] = useState(null);
  const blankItem = { type:"flight", description:"", date:"", time:"", confirmationNum:"", cost:"", booked:false, notes:"" };
  const [form, setForm] = useState(blankItem);
  function update(key, val) { setHoneymoon(prev=>({...prev,[key]:val})); }
  const items = honeymoon.items||[];
  function openAdd(type) { setForm({...blankItem,type}); setItemType(type); setEditing(null); setModal(true); }
  function openEdit(item) { setForm({...item}); setItemType(item.type); setEditing(item.id); setModal(true); }
  function save() { if(!form.description) return; const item={...form,cost:Number(form.cost),id:editing||uid()}; const updated = editing ? items.map(x=>x.id===editing?item:x) : [...items,item]; setHoneymoon(prev=>({...prev,items:updated})); setModal(false); }
  function remove(id) { setHoneymoon(prev=>({...prev,items:items.filter(x=>x.id!==id)})); }
  const TYPE_COLORS = { flight:"var(--sage)", hotel:"var(--gold)", activity:"var(--rose)", restaurant:"#9a7aaa", transport:"var(--ink-dim)", other:"var(--ink-dim)" };
  const flights = items.filter(i=>i.type==="flight");
  const hotels = items.filter(i=>i.type==="hotel");
  const activities = items.filter(i=>!["flight","hotel"].includes(i.type));
  const totalCost = items.reduce((s,i)=>s+Number(i.cost||0),0);
  const bookedCount = items.filter(i=>i.booked).length;

  return (
    <div>
      <div className="card-title" style={{margin:"0 0 20px"}}><Icon name="moon" size={16} color="var(--gold)"/>Honeymoon Planner</div>
      <div className="filter-bar">
        {[["overview","Overview"],["flights","Flights"],["hotels","Hotels"],["activities","Activities & Dining"],["packing","Packing List"],["docs","Documents"]].map(([id,label])=>(
          <button key={id} className={`filter-chip ${activeSection===id?"active":""}`} onClick={()=>setActiveSection(id)}>{label}</button>
        ))}
      </div>

      {activeSection==="overview" && (
        <div>
          <div className="stats-grid" style={{gridTemplateColumns:"repeat(4,1fr)",marginBottom:20}}>
            <div className="stat-card"><div className="stat-num">{items.length}</div><div className="stat-label">Total Items</div></div>
            <div className="stat-card"><div className="stat-num" style={{color:"var(--sage)"}}>{bookedCount}</div><div className="stat-label">Booked</div></div>
            <div className="stat-card"><div className="stat-num" style={{color:"#c04040"}}>{items.length-bookedCount}</div><div className="stat-label">To Book</div></div>
            <div className="stat-card"><div className="stat-num" style={{color:"var(--gold)"}}>{currency(totalCost)}</div><div className="stat-label">Est. Cost</div></div>
          </div>
          <div className="card" style={{marginBottom:14}}>
            <div className="card-title" style={{fontSize:16}}>Destination &amp; Dates</div>
            <div className="form-row">
              <div className="form-group"><label>Destination</label><input value={honeymoon.destination||""} onChange={e=>update("destination",e.target.value)} placeholder="e.g. Maldives, Bali, Italy"/></div>
              <div className="form-group"><label>Departure Date</label><input type="date" value={honeymoon.departureDate||""} onChange={e=>update("departureDate",e.target.value)}/></div>
              <div className="form-group"><label>Return Date</label><input type="date" value={honeymoon.returnDate||""} onChange={e=>update("returnDate",e.target.value)}/></div>
            </div>
            <div className="form-group"><label>Notes</label><textarea value={honeymoon.overview||""} onChange={e=>update("overview",e.target.value)} placeholder="General notes, ideas, things you want to do…" style={{minHeight:80}}/></div>
          </div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {[["flight","✈ Add Flight"],["hotel","🏨 Add Hotel"],["activity","🌟 Add Activity"],["restaurant","🍽 Add Restaurant"],["transport","🚗 Add Transport"]].map(([type,label])=>(
              <button key={type} className="btn btn-secondary" onClick={()=>openAdd(type)}>{label}</button>
            ))}
          </div>
        </div>
      )}

      {activeSection==="flights" && (
        <div>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}><button className="btn btn-primary" onClick={()=>openAdd("flight")}><Icon name="plus" size={13}/>Add Flight</button></div>
          {flights.length===0?<div className="card" style={{textAlign:"center",padding:40}}><p style={{color:"var(--ink-faint)",fontStyle:"italic"}}>No flights added yet</p></div>:
            flights.map(item => <HoneymoonItemCard key={item.id} item={item} onEdit={openEdit} onRemove={remove} color={TYPE_COLORS[item.type]}/>)
          }
        </div>
      )}
      {activeSection==="hotels" && (
        <div>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}><button className="btn btn-primary" onClick={()=>openAdd("hotel")}><Icon name="plus" size={13}/>Add Hotel</button></div>
          {hotels.length===0?<div className="card" style={{textAlign:"center",padding:40}}><p style={{color:"var(--ink-faint)",fontStyle:"italic"}}>No hotels added yet</p></div>:
            hotels.map(item => <HoneymoonItemCard key={item.id} item={item} onEdit={openEdit} onRemove={remove} color={TYPE_COLORS[item.type]}/>)
          }
        </div>
      )}
      {activeSection==="activities" && (
        <div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginBottom:14}}>
            <button className="btn btn-secondary" onClick={()=>openAdd("activity")}><Icon name="plus" size={13}/>Activity</button>
            <button className="btn btn-secondary" onClick={()=>openAdd("restaurant")}><Icon name="plus" size={13}/>Restaurant</button>
          </div>
          {activities.length===0?<div className="card" style={{textAlign:"center",padding:40}}><p style={{color:"var(--ink-faint)",fontStyle:"italic"}}>No activities added yet</p></div>:
            activities.map(item => <HoneymoonItemCard key={item.id} item={item} onEdit={openEdit} onRemove={remove} color={TYPE_COLORS[item.type]}/>)
          }
        </div>
      )}
      {activeSection==="packing" && (
        <div className="card">
          <div className="card-title" style={{fontSize:16}}>Packing List</div>
          <textarea value={honeymoon.packingList||""} onChange={e=>update("packingList",e.target.value)} placeholder={"Passports & travel docs\nCredit cards + travel card\nPhone chargers\nAdapter plugs\nSunscreen\nSwimwear\nFormal outfit for dinner\n…"} style={{width:"100%",minHeight:320,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.9,outline:"none",resize:"vertical"}}/>
        </div>
      )}
      {activeSection==="docs" && (
        <div className="card">
          <div className="card-title" style={{fontSize:16}}>Documents &amp; Reminders</div>
          <textarea value={honeymoon.docs||""} onChange={e=>update("docs",e.target.value)} placeholder={"Passport expiry dates:\nVisa requirements:\nTravel insurance policy #:\nEmergency contacts abroad:\nHotel addresses:\nAirline booking references:\nCurrency / exchange notes:\n…"} style={{width:"100%",minHeight:280,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.9,outline:"none",resize:"vertical"}}/>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-title"><Icon name="moon" size={16} color="var(--rose)"/>{editing?"Edit":"Add"} {itemType.charAt(0).toUpperCase()+itemType.slice(1)}</div>
            <div className="form-group" style={{marginBottom:14}}><label>Description *</label><input value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder={itemType==="flight"?"e.g. JAX → MIA → Maldives":itemType==="hotel"?"e.g. Four Seasons Maldives":"e.g. Sunset cruise"}/></div>
            <div className="form-row"><div className="form-group"><label>Date</label><input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))}/></div><div className="form-group"><label>Time</label><input type="time" value={form.time} onChange={e=>setForm(p=>({...p,time:e.target.value}))}/></div><div className="form-group"><label>Cost ($)</label><input type="number" value={form.cost} onChange={e=>setForm(p=>({...p,cost:e.target.value}))}/></div></div>
            <div className="form-group" style={{marginBottom:14}}><label>Confirmation #</label><input value={form.confirmationNum} onChange={e=>setForm(p=>({...p,confirmationNum:e.target.value}))}/></div>
            <label style={{display:"flex",gap:8,fontSize:12,cursor:"pointer",color:"var(--ink-dim)",alignItems:"center",marginBottom:14}}><input type="checkbox" checked={form.booked} onChange={e=>setForm(p=>({...p,booked:e.target.checked}))}/>Booked / Confirmed</label>
            <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>{editing?"Save":"Add"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function HoneymoonItemCard({ item, onEdit, onRemove, color }) {
  return (
    <div style={{background:"var(--forest-card)",border:`1px solid ${item.booked?"rgba(122,170,128,0.25)":"var(--border)"}`,borderRadius:"var(--radius)",padding:16,marginBottom:10,display:"flex",gap:14,alignItems:"flex-start"}}>
      <div style={{width:36,height:36,borderRadius:"50%",background:"var(--forest-lift)",border:`1px solid ${color}33`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <span style={{fontSize:14}}>{item.type==="flight"?"✈":item.type==="hotel"?"🏨":item.type==="restaurant"?"🍽":"🌟"}</span>
      </div>
      <div style={{flex:1}}>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:4}}>
          <span style={{fontWeight:600,fontSize:13,color:"var(--rose-light)"}}>{item.description}</span>
          <span className="badge" style={{background:item.booked?"rgba(122,170,128,0.15)":"rgba(192,64,64,0.12)",color:item.booked?"var(--sage)":"#c06060",fontSize:9}}>{item.booked?"✦ Booked":"Not booked"}</span>
        </div>
        <div style={{fontSize:12,color:"var(--ink-dim)"}}>
          {item.date&&<span>{item.date}</span>}{item.time&&<span> · {item.time}</span>}
          {item.confirmationNum&&<span> · Conf: {item.confirmationNum}</span>}
          {item.cost>0&&<span style={{color:"var(--gold)",marginLeft:8}}>{currency(item.cost)}</span>}
        </div>
        {item.notes&&<div style={{fontSize:11,color:"var(--ink-faint)",fontStyle:"italic",marginTop:4}}>{item.notes}</div>}
      </div>
      <div style={{display:"flex",gap:4,flexShrink:0}}>
        <button className="btn-icon" onClick={()=>onEdit(item)}><Icon name="edit" size={13}/></button>
        <button className="btn-icon" onClick={()=>onRemove(item.id)}><Icon name="trash" size={13}/></button>
      </div>
    </div>
  );
}

// ── Accommodations Tab ────────────────────────────────────────────────────────
function AccommodationsTab({ accommodations, setAccommodations }) {
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { guestName:"", hotel:"", checkIn:"", checkOut:"", confirmationNum:"", roomType:"", needsShuttle:false, shuttleTime:"", notes:"" };
  const [form, setForm] = useState(blank);
  const [hotelFilter, setHotelFilter] = useState("All");
  const [blockInfo, setBlockInfo] = useState(accommodations.blockInfo||"");
  const hotels = ["All",...new Set(accommodations.guests?.map(a=>a.hotel)||[])];
  const guests = accommodations.guests||[];
  const filtered = hotelFilter==="All" ? guests : guests.filter(a=>a.hotel===hotelFilter);
  const needsShuttle = guests.filter(a=>a.needsShuttle).length;
  function save() { if(!form.guestName) return; const a={...form,id:editing||uid()}; const updated=editing?guests.map(x=>x.id===editing?a:x):[...guests,a]; setAccommodations(prev=>({...prev,guests:updated})); setModal(false); }
  function remove(id) { setAccommodations(prev=>({...prev,guests:guests.filter(x=>x.id!==id)})); }
  function openAdd() { setForm(blank); setEditing(null); setModal(true); }
  function openEdit(a) { setForm({...a}); setEditing(a.id); setModal(true); }

  return (
    <div>
      <div className="sec-header">
        <div className="card-title" style={{margin:0}}><Icon name="venue" size={16} color="var(--gold)"/>Accommodations</div>
        <button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={13}/>Add Guest</button>
      </div>
      <div className="stats-grid" style={{gridTemplateColumns:"repeat(3,1fr)",marginBottom:20}}>
        <div className="stat-card"><div className="stat-num">{guests.length}</div><div className="stat-label">Guests Tracked</div></div>
        <div className="stat-card"><div className="stat-num">{[...new Set(guests.map(g=>g.hotel).filter(Boolean))].length}</div><div className="stat-label">Hotels</div></div>
        <div className="stat-card"><div className="stat-num" style={{color:"var(--gold)"}}>{needsShuttle}</div><div className="stat-label">Need Shuttle</div></div>
      </div>
      <div className="card" style={{marginBottom:14}}>
        <div className="card-title" style={{fontSize:16}}>Room Block Info</div>
        <textarea value={blockInfo} onChange={e=>{setBlockInfo(e.target.value);setAccommodations(prev=>({...prev,blockInfo:e.target.value}));}} placeholder={"Hotel name, address, block code, cutoff date, group rate, contact name & phone…"} style={{width:"100%",minHeight:100,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.7,outline:"none",resize:"vertical"}}/>
      </div>
      <div className="filter-bar">{hotels.map(h=><button key={h} className={`filter-chip ${hotelFilter===h?"active":""}`} onClick={()=>setHotelFilter(h)}>{h}</button>)}</div>
      <div className="card" style={{padding:0}}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Guest</th><th>Hotel</th><th>Check In</th><th>Check Out</th><th>Room</th><th>Confirmation</th><th>Shuttle</th><th></th></tr></thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id}>
                  <td style={{fontWeight:600,fontSize:13,color:"var(--rose-light)"}}>{a.guestName}</td>
                  <td style={{fontSize:12}}>{a.hotel||"—"}</td>
                  <td style={{fontSize:12,color:"var(--ink-dim)"}}>{a.checkIn||"—"}</td>
                  <td style={{fontSize:12,color:"var(--ink-dim)"}}>{a.checkOut||"—"}</td>
                  <td style={{fontSize:12}}>{a.roomType||"—"}</td>
                  <td style={{fontSize:11,color:"var(--ink-dim)"}}>{a.confirmationNum||"—"}</td>
                  <td><span className="badge" style={{background:a.needsShuttle?"rgba(200,169,110,0.15)":"transparent",color:a.needsShuttle?"var(--gold)":"var(--ink-faint)",fontSize:9}}>{a.needsShuttle ? `✦ ${a.shuttleTime||"Yes"}` : "No"}</span></td>
                  <td><div style={{display:"flex",gap:4}}><button className="btn-icon" onClick={()=>openEdit(a)}><Icon name="edit" size={13}/></button><button className="btn-icon" onClick={()=>remove(a.id)}><Icon name="trash" size={13}/></button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length===0&&<div className="empty-state" style={{padding:40}}><Icon name="venue" size={36} color="var(--ink-faint)"/><p style={{marginTop:10,color:"var(--ink-faint)"}}>Track where out-of-town guests are staying</p></div>}
        </div>
      </div>
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-title"><Icon name="venue" size={16} color="var(--rose)"/>{editing?"Edit":"Add Guest Accommodation"}</div>
            <div className="form-row"><div className="form-group" style={{flex:2}}><label>Guest Name *</label><input value={form.guestName} onChange={e=>setForm(p=>({...p,guestName:e.target.value}))}/></div><div className="form-group"><label>Hotel</label><input value={form.hotel} onChange={e=>setForm(p=>({...p,hotel:e.target.value}))}/></div></div>
            <div className="form-row"><div className="form-group"><label>Check In</label><input type="date" value={form.checkIn} onChange={e=>setForm(p=>({...p,checkIn:e.target.value}))}/></div><div className="form-group"><label>Check Out</label><input type="date" value={form.checkOut} onChange={e=>setForm(p=>({...p,checkOut:e.target.value}))}/></div><div className="form-group"><label>Room Type</label><input value={form.roomType} onChange={e=>setForm(p=>({...p,roomType:e.target.value}))} placeholder="e.g. King, Double"/></div></div>
            <div className="form-group" style={{marginBottom:14}}><label>Confirmation #</label><input value={form.confirmationNum} onChange={e=>setForm(p=>({...p,confirmationNum:e.target.value}))}/></div>
            <label style={{display:"flex",gap:8,fontSize:12,cursor:"pointer",color:"var(--ink-dim)",alignItems:"center",marginBottom:14}}><input type="checkbox" checked={form.needsShuttle} onChange={e=>setForm(p=>({...p,needsShuttle:e.target.checked}))}/>Needs shuttle</label>
            {form.needsShuttle&&<div className="form-group" style={{marginBottom:14}}><label>Shuttle Time</label><input value={form.shuttleTime} onChange={e=>setForm(p=>({...p,shuttleTime:e.target.value}))} placeholder="e.g. 6:30 PM pickup"/></div>}
            <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>{editing?"Save":"Add"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Decor Tab ─────────────────────────────────────────────────────────────────
function DecorTab({ decor, setDecor }) {
  function update(key, val) { setDecor(prev=>({...prev,[key]:val})); }
  const [activeSection, setActiveSection] = useState("palette");
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const CATS = ["Florals","Centerpieces","Linens","Lighting","Ceremony Arch","Tables","Candles","Signage","Other"];
  const blank = { item:"", category:"Florals", description:"", vendor:"", cost:"", confirmed:false, notes:"" };
  const [form, setForm] = useState(blank);
  const items = decor.items||[];
  function openAdd() { setForm(blank); setEditing(null); setModal(true); }
  function openEdit(item) { setForm({...item}); setEditing(item.id); setModal(true); }
  function save() { if(!form.item) return; const i={...form,cost:Number(form.cost),id:editing||uid()}; const updated=editing?items.map(x=>x.id===editing?i:x):[...items,i]; setDecor(prev=>({...prev,items:updated})); setModal(false); }
  function remove(id) { setDecor(prev=>({...prev,items:items.filter(x=>x.id!==id)})); }

  return (
    <div>
      <div className="card-title" style={{margin:"0 0 20px"}}><Icon name="flower" size={16} color="var(--gold)"/>Decor &amp; Styling</div>
      <div className="filter-bar">
        {[["palette","Color Palette"],["items","Decor Items"],["flowers","Florals"],["notes","Style Notes"]].map(([id,label])=>(
          <button key={id} className={`filter-chip ${activeSection===id?"active":""}`} onClick={()=>setActiveSection(id)}>{label}</button>
        ))}
      </div>

      {activeSection==="palette" && (
        <div className="card">
          <div className="card-title" style={{fontSize:16}}>Color Palette &amp; Theme</div>
          <div className="form-group" style={{marginBottom:14}}><label>Wedding Theme</label><input value={decor.theme||""} onChange={e=>update("theme",e.target.value)} placeholder="e.g. Enchanted Garden, Modern Romantic, Boho Luxe"/></div>
          <div className="form-group" style={{marginBottom:14}}><label>Primary Colors</label><input value={decor.primaryColors||""} onChange={e=>update("primaryColors",e.target.value)} placeholder="e.g. Sage green, dusty rose, champagne gold"/></div>
          <div className="form-group" style={{marginBottom:14}}><label>Accent Colors</label><input value={decor.accentColors||""} onChange={e=>update("accentColors",e.target.value)} placeholder="e.g. Ivory, warm white, deep forest green"/></div>
          <div className="form-group" style={{marginBottom:14}}><label>Overall Vibe / Mood</label><textarea value={decor.mood||""} onChange={e=>update("mood",e.target.value)} placeholder="Describe the feeling you want — romantic, ethereal, lush, minimalist, moody…" style={{minHeight:80}}/></div>
          <div className="form-group"><label>Inspiration Notes</label><textarea value={decor.inspiration||""} onChange={e=>update("inspiration",e.target.value)} placeholder="Links, Pinterest boards, specific images, designers you love…" style={{minHeight:80}}/></div>
        </div>
      )}
      {activeSection==="items" && (
        <div>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}><button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={13}/>Add Item</button></div>
          {items.length===0?<div className="card" style={{textAlign:"center",padding:40}}><Icon name="flower" size={36} color="var(--ink-faint)"/><p style={{color:"var(--ink-faint)",marginTop:10,fontStyle:"italic"}}>Track centerpieces, linens, candles, signage and more</p></div>:
            items.map(item=>(
              <div key={item.id} style={{background:"var(--forest-card)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:16,marginBottom:10,display:"flex",gap:14,alignItems:"flex-start"}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:4}}>
                    <span style={{fontWeight:600,fontSize:13,color:"var(--rose-light)"}}>{item.item}</span>
                    <span className="badge" style={{background:"rgba(200,145,122,0.1)",color:"var(--rose)",fontSize:9}}>{item.category}</span>
                    <span className="badge" style={{background:item.confirmed?"rgba(122,170,128,0.15)":"rgba(175,155,105,0.1)",color:item.confirmed?"var(--sage)":"var(--ink-dim)",fontSize:9}}>{item.confirmed?"✦ Confirmed":"Pending"}</span>
                  </div>
                  {item.description&&<div style={{fontSize:12,color:"var(--ink)",marginTop:2}}>{item.description}</div>}
                  <div style={{fontSize:11,color:"var(--ink-dim)",marginTop:4}}>{item.vendor&&`Vendor: ${item.vendor}`}{item.cost>0&&` · ${currency(item.cost)}`}</div>
                </div>
                <div style={{display:"flex",gap:4}}><button className="btn-icon" onClick={()=>openEdit(item)}><Icon name="edit" size={13}/></button><button className="btn-icon" onClick={()=>remove(item.id)}><Icon name="trash" size={13}/></button></div>
              </div>
            ))
          }
        </div>
      )}
      {activeSection==="flowers" && (
        <div className="card">
          <div className="card-title" style={{fontSize:16}}>Floral Notes</div>
          <textarea value={decor.floralNotes||""} onChange={e=>update("floralNotes",e.target.value)} placeholder={"Florist: \nBridal bouquet: \nBridesmaid bouquets: \nBoutonnieres: \nCeremony arch flowers: \nCenterpieces: \nFlower girls petals: \nFloral budget: \nPreferred flowers: \nFlowers to avoid: "} style={{width:"100%",minHeight:300,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.9,outline:"none",resize:"vertical"}}/>
        </div>
      )}
      {activeSection==="notes" && (
        <div className="card">
          <div className="card-title" style={{fontSize:16}}>Style Notes &amp; Ideas</div>
          <textarea value={decor.styleNotes||""} onChange={e=>update("styleNotes",e.target.value)} placeholder="Any other decor ideas, things to remember, specific requests for vendors…" style={{width:"100%",minHeight:280,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.8,outline:"none",resize:"vertical"}}/>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-title"><Icon name="flower" size={16} color="var(--rose)"/>{editing?"Edit Item":"Add Decor Item"}</div>
            <div className="form-row"><div className="form-group" style={{flex:2}}><label>Item *</label><input value={form.item} onChange={e=>setForm(p=>({...p,item:e.target.value}))} placeholder="e.g. Head table centerpiece"/></div><div className="form-group"><label>Category</label><select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>{CATS.map(c=><option key={c}>{c}</option>)}</select></div></div>
            <div className="form-group" style={{marginBottom:14}}><label>Description</label><textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="Details, colors, style notes…"/></div>
            <div className="form-row"><div className="form-group"><label>Vendor</label><input value={form.vendor} onChange={e=>setForm(p=>({...p,vendor:e.target.value}))}/></div><div className="form-group"><label>Cost ($)</label><input type="number" value={form.cost} onChange={e=>setForm(p=>({...p,cost:e.target.value}))}/></div></div>
            <label style={{display:"flex",gap:8,fontSize:12,cursor:"pointer",color:"var(--ink-dim)",alignItems:"center",marginBottom:14}}><input type="checkbox" checked={form.confirmed} onChange={e=>setForm(p=>({...p,confirmed:e.target.checked}))}/>Confirmed with vendor</label>
            <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>{editing?"Save":"Add"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Day Of Tab ────────────────────────────────────────────────────────────────
function DayOfTab({ dayOf, setDayOf }) {
  function update(key, val) { setDayOf(prev=>({...prev,[key]:val})); }
  const contacts = dayOf.contacts||"";
  const kit = dayOf.kit||"";

  return (
    <div>
      <div className="card-title" style={{margin:"0 0 20px"}}><Icon name="sparkle" size={16} color="var(--gold)"/>Day-of Emergency Card</div>
      <div style={{marginBottom:14,padding:"12px 16px",background:"rgba(200,169,110,0.08)",borderRadius:8,border:"1px solid rgba(200,169,110,0.2)",fontSize:12,color:"var(--gold)"}}>
        ✦ Screenshot this page on the morning of your wedding so you have everything offline
      </div>
      <div className="grid-2">
        <div className="card">
          <div className="card-title" style={{fontSize:16}}>Emergency Contacts</div>
          <textarea value={contacts} onChange={e=>update("contacts",e.target.value)} placeholder={"Venue coordinator: [name] [phone]\nCatering manager: [name] [phone]\nPhotographer: [name] [phone]\nVideographer: [name] [phone]\nDJ / Band: [name] [phone]\nFlower delivery: [name] [phone]\nOfficiant: [name] [phone]\nTransportation: [name] [phone]\nMOH: [name] [phone]\nBest Man: [name] [phone]\nParent contacts: [name] [phone]\nNearest urgent care: [address]\nNearest hospital: [address]"} style={{width:"100%",minHeight:320,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.9,outline:"none",resize:"vertical"}}/>
        </div>
        <div className="card">
          <div className="card-title" style={{fontSize:16}}>Emergency Kit Checklist</div>
          <textarea value={kit} onChange={e=>update("kit",e.target.value)} placeholder={"WHO HAS WHAT:\n☐ Wedding rings — [name]\n☐ Marriage license — [name]\n☐ Officiant payment envelope — [name]\n☐ Vendor tip envelopes — [name]\n☐ Bride's emergency kit (safety pins, stain remover, etc.) — [name]\n☐ Phone chargers — [name]\n☐ Bridal party gifts — [name]\n\nVENUE:\n☐ Venue address: \n☐ Parking info: \n☐ Load-in time: \n☐ Coordinator on-site: \n\nTIMING:\n☐ Hair & makeup starts: \n☐ Photos begin: \n☐ First look: \n☐ Ceremony begins: \n☐ Reception ends: "} style={{width:"100%",minHeight:320,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.9,outline:"none",resize:"vertical"}}/>
        </div>
      </div>
      <div className="card" style={{marginTop:14}}>
        <div className="card-title" style={{fontSize:16}}>Important Notes</div>
        <textarea value={dayOf.notes||""} onChange={e=>update("notes",e.target.value)} placeholder="Anything else that matters on the day — vendor instructions, family dynamics to be aware of, specific shot list reminders, last-minute to-dos…" style={{width:"100%",minHeight:120,padding:12,borderRadius:8,border:"1px solid var(--border)",background:"var(--forest-lift)",color:"var(--ink)",fontFamily:"Raleway",fontSize:13,lineHeight:1.8,outline:"none",resize:"vertical"}}/>
      </div>
    </div>
  );
}

// ── Invitation Tracker Tab ────────────────────────────────────────────────────
function InvitationsTab({ invitations, setInvitations }) {
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const blank = { name:"", address:"", saveDateSent:false, saveDateDate:"", inviteSent:false, inviteDate:"", digital:false, plusOne:"", notes:"" };
  const [form, setForm] = useState(blank);
  const filtered = filter==="all"?invitations:filter==="save-date"?invitations.filter(i=>!i.saveDateSent):filter==="invite"?invitations.filter(i=>i.saveDateSent&&!i.inviteSent):invitations.filter(i=>i.inviteSent);
  const savesSent = invitations.filter(i=>i.saveDateSent).length;
  const invitesSent = invitations.filter(i=>i.inviteSent).length;
  function openAdd() { setForm(blank); setEditing(null); setModal(true); }
  function openEdit(inv) { setForm({...inv}); setEditing(inv.id); setModal(true); }
  function save() { if(!form.name) return; const inv={...form,id:editing||uid()}; if(editing) setInvitations(p=>p.map(x=>x.id===editing?inv:x)); else setInvitations(p=>[...p,inv]); setModal(false); }
  function remove(id) { setInvitations(p=>p.filter(i=>i.id!==id)); }

  return (
    <div>
      <div className="sec-header">
        <div className="card-title" style={{margin:0}}><Icon name="feather" size={16} color="var(--gold)"/>Invitation Tracker</div>
        <button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={13}/>Add Guest</button>
      </div>
      <div className="stats-grid" style={{gridTemplateColumns:"repeat(4,1fr)",marginBottom:20}}>
        <div className="stat-card"><div className="stat-num">{invitations.length}</div><div className="stat-label">Total</div></div>
        <div className="stat-card"><div className="stat-num" style={{color:"var(--sage)"}}>{savesSent}</div><div className="stat-label">Save the Dates Sent</div></div>
        <div className="stat-card"><div className="stat-num" style={{color:"var(--gold)"}}>{invitesSent}</div><div className="stat-label">Invites Sent</div></div>
        <div className="stat-card"><div className="stat-num" style={{color:"#c04040"}}>{invitations.length-invitesSent}</div><div className="stat-label">Invites Pending</div></div>
      </div>
      <div className="filter-bar">
        {[["all","All"],["save-date","Save the Date Pending"],["invite","Invite Pending"],["done","All Sent"]].map(([val,label])=>(
          <button key={val} className={`filter-chip ${filter===val?"active":""}`} onClick={()=>setFilter(val)}>{label}</button>
        ))}
      </div>
      <div className="card" style={{padding:0}}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Address</th><th>Save the Date</th><th>Invitation</th><th>Digital</th><th>Plus One</th><th></th></tr></thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.id}>
                  <td style={{fontWeight:600,fontSize:13,color:"var(--rose-light)"}}>{inv.name}</td>
                  <td style={{fontSize:11,color:"var(--ink-dim)",maxWidth:160}}>{inv.address||"—"}</td>
                  <td><span className="badge" style={{background:inv.saveDateSent?"rgba(122,170,128,0.15)":"rgba(175,155,105,0.1)",color:inv.saveDateSent?"var(--sage)":"var(--ink-dim)",fontSize:9}}>{inv.saveDateSent?`✦ ${inv.saveDateDate||"Sent"}`:"Pending"}</span></td>
                  <td><span className="badge" style={{background:inv.inviteSent?"rgba(122,170,128,0.15)":"rgba(192,64,64,0.12)",color:inv.inviteSent?"var(--sage)":"#c06060",fontSize:9}}>{inv.inviteSent?`✦ ${inv.inviteDate||"Sent"}`:"Not sent"}</span></td>
                  <td style={{fontSize:12}}>{inv.digital?"Digital":"Physical"}</td>
                  <td style={{fontSize:12,color:"var(--ink-dim)"}}>{inv.plusOne||"—"}</td>
                  <td><div style={{display:"flex",gap:4}}><button className="btn-icon" onClick={()=>openEdit(inv)}><Icon name="edit" size={13}/></button><button className="btn-icon" onClick={()=>remove(inv.id)}><Icon name="trash" size={13}/></button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length===0&&<div className="empty-state" style={{padding:40}}><Icon name="feather" size={36} color="var(--ink-faint)"/><p style={{marginTop:10,color:"var(--ink-faint)"}}>Track save the dates and invitations</p></div>}
        </div>
      </div>
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-title"><Icon name="feather" size={16} color="var(--rose)"/>{editing?"Edit":"Add Guest"}</div>
            <div className="form-group" style={{marginBottom:14}}><label>Name *</label><input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/></div>
            <div className="form-group" style={{marginBottom:14}}><label>Address</label><textarea value={form.address} onChange={e=>setForm(p=>({...p,address:e.target.value}))} style={{minHeight:60}} placeholder="Mailing address for physical invites"/></div>
            <div className="form-row">
              <div className="form-group"><label>Plus One Name</label><input value={form.plusOne} onChange={e=>setForm(p=>({...p,plusOne:e.target.value}))} placeholder="If applicable"/></div>
            </div>
            <label style={{display:"flex",gap:8,fontSize:12,cursor:"pointer",color:"var(--ink-dim)",alignItems:"center",marginBottom:14}}><input type="checkbox" checked={form.digital} onChange={e=>setForm(p=>({...p,digital:e.target.checked}))}/>Digital invitation (no physical mail)</label>
            <div className="form-row">
              <div className="form-group"><label>Save the Date Sent</label><input type="date" value={form.saveDateDate} onChange={e=>setForm(p=>({...p,saveDateDate:e.target.value,saveDateSent:!!e.target.value}))}/></div>
              <div className="form-group"><label>Invitation Sent</label><input type="date" value={form.inviteDate} onChange={e=>setForm(p=>({...p,inviteDate:e.target.value,inviteSent:!!e.target.value}))}/></div>
            </div>
            <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>{editing?"Save":"Add"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
